import { EnterHook, PlainRoute, RouterState, browserHistory, match, useRouterHistory } from 'react-router';
import { LocationDescriptor, LocationDescriptorObject } from 'history';
import { ComponentClass, ComponentElement, ReactElement, StatelessComponent, isValidElement } from 'react';
import { ComponentLoader, Forbidden, NoServicesAvailable, Root } from 'components/routing';
import Spinner from 'components/common/Spinner/Spinner';
import { Resource } from 'typed/components/common/FormattedResource/FormattedResource';
import store, { State as Store } from 'store'; // TODO remove; store and dispatch should always come from those who want to get actual route parameters
import {
    AccessFunctionality, AccessFunctionalityAddOn, ForecastModelDataEntryType, GroupCollaborationEntityOwner, GroupCollaborationEntityStatus, GroupCollaborationEntityType,
    NewsEntryEntryStatus, PerformancePerspectiveGroup, ProductPackage, RequestStatus, ScenarioView, TrackingLevel, UserRoleBillingUserType, entities
} from 'typed/api';
import defaults from 'api/defaults';
import { entityToQuery, objectToQuery } from 'api/utils';
import * as actions from 'typed/actions';
import * as untypedActions from 'actions';
import {
    AccessType, IntegrationSource, MEETING_PRESENTATION_DATA_SOURCE_STORE_KEYS_MAP, MeetingPresentationDataSource,
    NewsEntrySourceCategory, PersistentNewsfeedKey, RankingScope
} from 'utils/constants';
import { Params } from 'react-router/lib/Router';
import { getCorporateStructureLinkDescription } from 'typed/components/corporate-structure/utils';
import { DefaultStrategyFilter, convertStrategyFilterToQuery } from 'typed/components/strategy/utils';

type Query = LocationDescriptorObject['query'];

export const history = useRouterHistory(() => browserHistory)({ basename: ROOT_PATH.replace(/\/+$/, '') });

export enum RouteAccessType {
    /** Act as if route wouldn't exist in the application - no menu items, NotFound at specified location. */
    HIDDEN = 'HIDDEN',
    /** The route exists, but user can't reach it - disabled menu items, Forbidden at specified location. */
    DISABLED = 'DISABLED',
    /** User can only look at the page. Routing thingie does not enforce this restriction, but pass {@link readonly} flag to a component. */
    READONLY = 'READONLY',
    /** Normal access. */
    FULL = 'FULL'
}
const isRouteAccessType = (v: any): v is RouteAccessType => (RouteAccessType as any)[v] === v;


type Dispatch = (typeof store)['dispatch'];

export const UNKNOWN = Symbol('UNKNOWN');

interface StoreExtractor<T> {
    /**
    * @callback StoreExtractor
    * @returns {T} - some property value based on current state of the app
    */
    (params: Params, store: Store, dispatch: Dispatch): T | typeof UNKNOWN;
}

const
    isStoreExtractor = <T extends any>(v: T | StoreExtractor<T> | null | undefined): v is StoreExtractor<T> => typeof v === 'function',
    USER_ADMINISTRATION = Symbol('USER_ADMINISTRATION');

interface PackageRouteAccessType {
    functionality: typeof USER_ADMINISTRATION | AccessFunctionality;
    addOnID?: AccessFunctionalityAddOn;
    [key: number]: RouteAccessType | StoreExtractor<RouteAccessType> | undefined;
}

type RouteOption<T> = T | StoreExtractor<T>;
const
    stateify = <T extends any>(value: RouteOption<T>): StoreExtractor<T> => isStoreExtractor(value) ? value : () => value,

    ROUTE_ACCESS_TYPE_PREFERENCE: { [P in RouteAccessType | typeof UNKNOWN]: number } = {
        [RouteAccessType.HIDDEN]: 0,
        [RouteAccessType.DISABLED]: 1,
        [RouteAccessType.READONLY]: 2,
        [RouteAccessType.FULL]: 2,
        [UNKNOWN]: 3
    };


interface MenuOptions {
    /** When user enters the route or one of it's descendants, override main menu with current route's children; if there are many root routes in the hierarchy, the deepest will be chosen. */
    root?: RouteOption<boolean>;
    /** Don't show route and all of it's descendants as items of a menu; does not affect when item acts as {@link root}. */
    hidden?: RouteOption<boolean>;
    /** Index of the item (or items group in case of {@link paramsCount} > 1) in submenu. */
    index?: RouteOption<number>;
    /** Used to determine should left side menu show a separator between link */
    groupIndex?: RouteOption<number>;
    /** Show +/- icon in left menu. Icon is not visible if the menu item has no children items, but is visible if they are {@link UNKNOWN}. */
    collapsable?: RouteOption<boolean>;
    /** Amount of items to be returned by {@link params}. It should only be provided if it's possible to get the count without actual data loading (e. g. using PaginationSlice without data). */
    paramsCount?: RouteOption<number>;
    /** A list of sets of params which might be passed to URL. As state extractor should return only possible combinations with passed params. A menu item with full URL with resolved params will be generated for each set. */
    params?: RouteOption<Params[]>;
    /** A query to always add to menu item's URL. Does not affect "are we at this route" check. */
    query?: RouteOption<Query>;
    /** A number that is displayed in the left side menu */
    itemCount?: RouteOption<number>;
    /** The font awesome icon that will be displayed before the link */
    icon?: RouteOption<string>;
    /* Hides the extra padding from Layout's content-area. In fullscreen it removes the padding from content as well. */
    hidePadding?: RouteOption<boolean>;
    /* Action for collapsable routes. Mainly used to load the entities needed for generating the child routes */
    onExpand?: any;
}

interface ConfigRouteCommon {
    path?: string;
    /** A title to be shown in browser tab, menus, page header etc. */
    title?: RouteOption<Resource>;
    /** Menu specific configuration. */
    menuOptions?: MenuOptions;

    childRoutes?: ConfigRoute[];

    /**
     * There is no counterpart to PlainRoute.getChildRoutes since current access control implementation does not support asynchronicity.
     * If you need to build a set of routes based on user data, use path params and {@link params} option.
     * Check buildRuntimeRoutes for more details.
     */
    getChildRoutes?: never;
}

interface ConfigRedirectRoute extends ConfigRouteCommon {
    /**
     * LocationDescriptor to redirect to.
     * {@default} the first child
     */
    redirect: LocationDescriptor;
}

interface ConfigAutoRedirectRoute extends ConfigRouteCommon {
    childRoutes: [ConfigRoute, ...ConfigRoute[]];
}

interface ConfigComponentRoute extends ConfigRouteCommon {
    title: RouteOption<Resource>;
    /** A component to be shown when current URL matches route's path. */
    component: ComponentClass | StatelessComponent | ReactElement;
    /**
     * There is no counterpart to PlainRoute.getComponent since current access control implementation does not support asynchronicity.
     * If you need to load a component asynchronosly, use ComponentLoader.
     * Check buildRuntimeRoutes for more details.
     */
    getComponent?: never;
    /** If user enters some descendant route, still show current route's component with descendant's one as children. */
    matchChildRoutes?: boolean;

    /**
    * Defines user permissions for a route, either explicitly or get user permissions for a specific AccessFunctionality.
    */
    access: RouteOption<RouteAccessType | AccessFunctionality>;
    /** Route's being in development, hide route from all non-administrators, show " (beta)" in title for all others. */
    beta?: RouteOption<boolean>;

    onEnter?: EnterHook; // TODO needed?
}

type ConfigRoute = ConfigComponentRoute | ConfigRedirectRoute | ConfigAutoRedirectRoute;
const isComponentRoute = (route: ConfigRoute): route is ConfigComponentRoute => 'component' in route;


interface RuntimeMenuOptions {
    getRoot: StoreExtractor<boolean>;
    getHidden: StoreExtractor<boolean>;
    getIndex: StoreExtractor<number | null>;
    getGroupIndex: StoreExtractor<number | null>;
    getCollapsable: StoreExtractor<boolean>;
    getParamsCount: StoreExtractor<number | null>;
    getParams: StoreExtractor<Params[] | null>;
    getQuery: StoreExtractor<Query>;
    getItemCount: StoreExtractor<number | null>;
    getIcon: StoreExtractor<string>;
    getHidePadding: StoreExtractor<boolean>;
    getOnExpand: StoreExtractor<any>;
}

export interface RuntimeRoute extends PlainRoute {
    _instance?: ReactElement;
    getTitle: StoreExtractor<Resource>;
    getBeta: StoreExtractor<boolean>;
    getAccess: StoreExtractor<RouteAccessType>;
    menuOptions: RuntimeMenuOptions;
    childRoutes: RuntimeRoute[];
}


// both of these has to be the same function instances across all routes object
// when reconciling React first ===s types of old and new component, then if they are equal, it compares what they render
// but if they are not equal, it unmounts old one and mounts new one even if they render exactly the same stuff
// so if we'd move these arrow function creation to prepareRoutes, every route change would force to all components to be unmounted and remounted
// though it would be prettier to use closure than to store internal data in route objects
const
    Children: StatelessComponent<{ children: ReactElement; }> = ({ children }) => children,
    /* eslint-disable no-underscore-dangle */
    UnwrappedInstance: StatelessComponent<{ router: RouterState; route: RuntimeRoute; }> = (props) => {
        const Type = props.route._instance!.type;
        return (
            <Type {...props.route._instance!.props} readonly={props.route.getAccess(props.router.params, store.getState(), store.dispatch) === RouteAccessType.READONLY} {...props}>
                {props.children}
            </Type>
        );
    },
    /* eslint-enable no-underscore-dangle */
    /**
 * Performs a set of transformations to make routes match react-router API, connect some fields to current session and make it easier to use in components.
 * 1. Transforms all plain properties to getProperty properties so components don't need to check which one exists and which one to use.
 * 2. Transforms all {@link access} returning {@link AccessFunctionality} to {@link getAccess} returning {@link boolean} according to current company modules and user profiles.
 * 3. Makes them explicitly return default value, if property is not set in configuration.
 * 4. Updates {@link access}, {@link component}, {@link menuOptions} based on {@link beta} and current session to make menus and links appear as if route did not exist.
 * 5. Transforms component instance as {@link component} to a functional component which extracts preset props from the instance, merges them with passed props and returns new instance with full set of props.
 * 6. Replaces {@link component} with simple functional component returning all children when {@link matchChildRoutes} is false:
 *      by default react-router always passes component of a route to a component of a parent route and so on, i. e.
 *      { path: 'top', component: Top, childRoutes: [{ path: 'top/middle', component: Middle, childRoutes: [{ path: 'top/middle/bottom', component: bottom }] }] }
 *      once entered the path "top/middle/bottom" will render
 *      <Top><Middle><Bottom/></Middle></Top>
 *      but in most cases we want to render only the exact component of the exact route
 *      it could be done by making routes structure flat, but we can't do it since it's also used to construct hierarchical menus
 *      the function replaces all non-matchChildRoutes with getComponent conditionally returning either route's component or it's child's component based on route's exact match
 * 7. Replaces {@link redirect} with react-router's IndexRoute.onEnter. If no {@link component} nor {@link redirect} are set, tries to use first child route as redirect path. Copies redirected route's accessibility properties.
 * @param {[ConfigRoute]} routes
 * @returns {[RuntimeRoute]}
 */
    buildRuntimeRoutes = (routes: ConfigRoute[]): RuntimeRoute[] => routes.map((route) => {
        if (!isComponentRoute(route)) {
            const
                {
                    path,
                    title = '',
                    menuOptions: {
                        root = false,
                        hidden = false,
                        index = null,
                        groupIndex = null,
                        collapsable = false,
                        paramsCount = null,
                        params = null,
                        query = {},
                        itemCount = null,
                        icon = '',
                        hidePadding = false,
                        onExpand = null
                    } = {}
                } = route,
                childRoutes = buildRuntimeRoutes(route.childRoutes || []),
                getTargetRoute: StoreExtractor<RuntimeRoute> = (params, store, dispatch) => {
                    if (!('redirect' in route)) {
                        return store.app.session.UserID ?
                            childRoutes.maxBy(route => ROUTE_ACCESS_TYPE_PREFERENCE[route.getAccess(params, store, dispatch)]) :
                            UNKNOWN;
                    }
                    else {
                        // match is implemented to work asynchronously, but if all routes are synchronous, it works synchronously: https://github.com/ReactTraining/react-router/issues/1207#issuecomment-240172647
                        // we have our own component to load chunk asynchronously which also provides some additional functionality, so we should always use it instead of react-router's getComponent
                        // if some route can't be find synchronously, we assume it's not access

                        let
                            result: RuntimeRoute | null = null,
                            location = {
                                pathname: Object.keys(params).reduce((path, key) => path!.replace(`:${key}`, params[key]), typeof route.redirect !== 'string' ? route.redirect.pathname : route.redirect),
                                query: typeof route.redirect !== 'string' ? route.redirect.query : {}
                            };

                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        const appRoutes = store.app.session.PublicPagesEnabled && store.app.session.IsPublicPageUser ? publicPageRoutes : allRoutes;

                        while (location) {
                            // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-loop-func
                            match({ location, routes: appRoutes }, (_error, redirect, routeProps) => {
                                location = redirect;
                                result = routeProps?.routes?.last();
                            });
                        }
                        return result || UNKNOWN;
                    }
                };

            return {
                path: path || '',
                indexRoute: 'redirect' in route ?
                    {
                        onEnter: (state, replace) => replace(
                            {
                                pathname: Object.keys(state.params).reduce((path, key) => path!.replace(`:${key}`, state.params[key]), typeof route.redirect !== 'string' ? route.redirect.pathname : route.redirect),
                                query: typeof route.redirect !== 'string' ? route.redirect.query : state.location.query
                            }
                        ),
                        onChange: (_, state, replace) => replace(
                            {
                                pathname: Object.keys(state.params).reduce((path, key) => path!.replace(`:${key}`, state.params[key]), typeof route.redirect !== 'string' ? route.redirect.pathname : route.redirect),
                                query: typeof route.redirect !== 'string' ? route.redirect.query : state.location.query
                            }
                        )
                    } :
                    {
                        onEnter: (state, replace) => {
                            const target = getTargetRoute(state.params, store.getState(), store.dispatch);
                            if (target !== UNKNOWN) {
                                const query = target.menuOptions.getQuery(state.params, store.getState(), store.dispatch);

                                replace({
                                    pathname: target.path,
                                    query: { ...state.location.query, ...query !== UNKNOWN ? query : {} }
                                });
                            }
                        },
                        onChange: (_, state, replace) => {
                            const target = getTargetRoute(state.params, store.getState(), store.dispatch);
                            if (target !== UNKNOWN) {
                                const query = target.menuOptions.getQuery(state.params, store.getState(), store.dispatch);

                                replace({
                                    pathname: target.path,
                                    query: { ...state.location.query, ...query !== UNKNOWN ? query : {} }
                                });
                            }
                        }
                    },
                getTitle: stateify(title),
                getBeta: (params, store, dispatch) => {
                    const target = getTargetRoute(params, store, dispatch);
                    return target !== UNKNOWN ? target.getBeta(params, store, dispatch) : UNKNOWN;
                },
                getAccess: (params, store, dispatch) => {
                    const target = getTargetRoute(params, store, dispatch);
                    return target !== UNKNOWN ? target.getAccess(params, store, dispatch) : UNKNOWN;
                },
                menuOptions: {
                    getRoot: stateify(root),
                    getHidden: stateify(hidden),
                    getIndex: stateify(index),
                    getGroupIndex: stateify(groupIndex),
                    getCollapsable: stateify(collapsable),
                    getParamsCount: paramsCount !== null || !isStoreExtractor(params) ?
                        stateify<number | null>(paramsCount ?? params?.length ?? 0) :
                        (...args) => {
                            const p = params(...args);
                            return p !== UNKNOWN ? p.length : UNKNOWN;
                        },
                    getParams: params ? stateify(params) : () => null,
                    getQuery: stateify(query),
                    getItemCount: stateify(itemCount),
                    getIcon: stateify(icon),
                    getHidePadding: stateify(hidePadding),
                    getOnExpand: stateify(onExpand)
                },
                childRoutes
            };
        }
        else {
            const
                {
                    path,
                    component,
                    matchChildRoutes = false,
                    title = '',
                    beta = false,
                    access = RouteAccessType.HIDDEN,
                    menuOptions: {
                        root = false,
                        hidden = false,
                        index = null,
                        groupIndex = null,
                        collapsable = false,
                        paramsCount = null,
                        params = null,
                        query = {},
                        itemCount = null,
                        icon = '',
                        hidePadding = false,
                        onExpand = null
                    } = {}
                } = route,
                momponent: ComponentClass | StatelessComponent = isValidElement(component) ?
                    UnwrappedInstance as StatelessComponent :
                    component as ComponentClass | StatelessComponent,
                getOwnComponent: PlainRoute['getComponent'] = (_, callback) => callback(null, momponent),
                getChildComponent: PlainRoute['getComponent'] = matchChildRoutes ?
                    getOwnComponent :
                    (_, callback) => callback(null, Children as StatelessComponent),
                getAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
                    const
                        t = isStoreExtractor(access) ? access(params, store, dispatch) : access,
                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        e = !isRouteAccessType(t) && t !== UNKNOWN ? accessFromSession(t)(params, store, dispatch) : t;
                    return e !== UNKNOWN ? !store.app.session.IsUser4SAdministrator && (isStoreExtractor(beta) ? beta(params, store, dispatch) : beta) ? RouteAccessType.HIDDEN : e : UNKNOWN;
                };


            return {
                path: path || '',
                _instance: isValidElement(route.component) ? route.component as ComponentElement<{}, any> : undefined,
                getComponent: (state, callback) => {
                    const access = getAccess(state.params, store.getState(), store.dispatch);
                    switch (true) {
                        case access === UNKNOWN:
                            return callback(null, Spinner);
                        case access === RouteAccessType.HIDDEN:
                            return callback(null, NoServicesAvailable);
                        case access === RouteAccessType.DISABLED:
                            return callback(null, Forbidden);
                        case state.routes.last().path !== path:
                            return getChildComponent!(state, callback);
                        default:
                            return getOwnComponent!(state, callback);
                    }
                },
                getTitle: stateify(title),
                getBeta: stateify(beta),
                getAccess,
                menuOptions: {
                    getRoot: stateify(root),
                    getHidden: stateify(hidden),
                    getIndex: stateify(index),
                    getGroupIndex: stateify(groupIndex),
                    getCollapsable: stateify(collapsable),
                    getParamsCount: paramsCount !== null || !isStoreExtractor(params) ?
                        stateify<number | null>(paramsCount ?? params?.length ?? 0) :
                        (...args) => {
                            const p = params(...args);
                            return p !== UNKNOWN ? p.length : UNKNOWN;
                        },
                    getParams: params ? stateify(params) : () => null,
                    getQuery: stateify(query),
                    getItemCount: stateify(itemCount),
                    getIcon: stateify(icon),
                    getHidePadding: stateify(hidePadding),
                    getOnExpand: stateify(onExpand)
                },
                onEnter: route.onEnter,
                childRoutes: buildRuntimeRoutes(route.childRoutes || [])
            };
        }
    }),

    /**
 * Generates {@link StoreExtractor} to extract some values in runtime from store.
 * @param {string} path - path in store to the value similar to JS code, i. e. 'app.session.Name'. If an intermediate object is an array, function will try to use getByID. It's also possible to use route's params instead of actual names or IDs: 'dashboards.pages.:pageID'.
 * @param {T} def - default value. Works only if the whole path was correct, but the result is falsy. If some intermediate objects in path do not exist, the function will return {@link UNKNOWN}.
 */
    fromStore = <T extends any>(path: string, def?: T): StoreExtractor<T> => (params, store) => {
        let storePart: any = store;
        const pathParts = path.split('.');
        while (pathParts.length > 1) {
            const pathPart = pathParts.shift()!;
            storePart = pathPart[0] === ':' ? storePart.getByID(+params[pathPart.slice(1)] || params[pathPart.slice(1)] || +pathPart.slice(1)) : storePart[pathPart];
            if (!storePart) {
                return UNKNOWN;
            }
        }
        return (pathParts[0][0] === ':' ? storePart.getByID(+params[pathParts[0].slice(1)] || params[pathParts[0].slice(1)] || +pathParts[0].slice(1)) : storePart[pathParts[0]]) || def;
    },

    accessFromSession = (functionality: typeof USER_ADMINISTRATION | AccessFunctionality, addOnID?: AccessFunctionalityAddOn): StoreExtractor<RouteAccessType> =>
        (_params, { app: { session } }) => {
            const f = session.FunctionalityAccesses.find((f: any) => f.FunctionalityID === functionality);
            switch (true) {
                case !session.UserID:
                    return UNKNOWN;
                case session.IsExternalUser:
                    return RouteAccessType.HIDDEN;
                case functionality === USER_ADMINISTRATION:
                    return session.IsUserAdministrator ? RouteAccessType.FULL : RouteAccessType.DISABLED;
                case !f:
                    return RouteAccessType.HIDDEN;
                case f!.AccessTypeID === AccessType.NO_ACCESS:
                    return RouteAccessType.DISABLED;
                case !!addOnID && !session.FunctionalityAddOns.includes(addOnID):
                    return session.ProductPackageID !== ProductPackage.SMART_STRATEGY ? RouteAccessType.DISABLED : RouteAccessType.HIDDEN;
                case f!.AccessTypeID === AccessType.READ:
                    return RouteAccessType.READONLY;
                default:
                    return RouteAccessType.FULL;
            }
        },
    accessFromPackage = (config: PackageRouteAccessType): StoreExtractor<RouteAccessType> =>
        (params, store, dispatch) => {
            const
                { functionality, addOnID } = config,
                productPackageID: ProductPackage = store.app.session.ProductPackageID,
                accessTypeExtractor = config[productPackageID];

            if (!!accessTypeExtractor && isRouteAccessType(accessTypeExtractor)) {
                return accessTypeExtractor;
            }
            else if (isStoreExtractor(accessTypeExtractor)) {
                return accessTypeExtractor(params, store, dispatch);
            }

            return accessFromSession(functionality, addOnID)(params, store, dispatch);
        },
    accessFromUserRole = (functionality: typeof USER_ADMINISTRATION | AccessFunctionality, ...roles: UserRoleBillingUserType[]): StoreExtractor<RouteAccessType> =>
        (params, store, dispatch) => {
            const { session } = store.app as any;

            if (session.UseUserRoleBasedBilling && !roles.includes(session.UserRoleBillingUserTypeID)) {
                return RouteAccessType.HIDDEN;
            }

            return accessFromSession(functionality)(params, store, dispatch);
        };


export const getDefaultNewsroomQuery: StoreExtractor<Query> = (_params, store) => ({
    scope: (store.news as any).sourcesScope,
    ...entityToQuery((store.news as any).sourcesFilter)
});
export const getPendingNewsRankingQuery: StoreExtractor<Query> = (_params, store) => ({
    scope: RankingScope.ALL,
    ...entityToQuery({
        ...defaults.NewsEntryFilter
    }),
    sortMode: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].sortMode
});
export const getStakeholdersNewsRankingQuery: StoreExtractor<Query> = (_params, store) => ({
    scope: RankingScope.ALL,
    ...entityToQuery({
        ...defaults.NewsEntryFilter,
        SourceCategoryIDs: [NewsEntrySourceCategory.STAKEHOLDERS],
        Period: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].filter.Period
    }),
    sortMode: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].sortMode
});
export const getSWOTNewsRankingQuery: StoreExtractor<Query> = (_params, store) => ({
    scope: RankingScope.ALL,
    ...entityToQuery({
        ...defaults.NewsEntryFilter,
        SourceCategoryIDs: [NewsEntrySourceCategory.THREATS, NewsEntrySourceCategory.OPPORTUNITIES],
        Period: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].filter.Period
    }),
    sortMode: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].sortMode
});
export const getAllNewsRankingQuery: StoreExtractor<Query> = (_params, store) => ({
    scope: RankingScope.ALL,
    ...entityToQuery({
        ...defaults.NewsEntryFilter,
        Period: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].filter.Period
    }),
    sortMode: (store.news as any).newsfeeds[PersistentNewsfeedKey.RANKING].sortMode
});

export const DEFAULT_TREND_TOPICS_QUERY: Query = entityToQuery({ ...defaults.NewsEntryFilter });
export const getDefaultStrategyElementSettingQuery: StoreExtractor<Query> = (_params, store) => objectToQuery({ collapsedFunctionsIDs: (store.strategy as any).timelineCollapsedGoalsIDs });
export const getDefaultNewWatchQuery: StoreExtractor<Query> = (_params, store) => ({
    ...entityToQuery((store.watches as any).sourcesFilter)
});
export const getDefaultMeetingOverviewQuery: StoreExtractor<Query> = (_params, _store) => objectToQuery({ actions: true });
export const getDefaultIndicatorListQuery: StoreExtractor<Query> = (_params, store) => objectToQuery((store.performanceIndicators as any).indicatorListSettings);
export const DEFAULT_AUTHORIZATION_PAGE_QUERY: Query = objectToQuery({ authorizations: true });

const
    NON_PARTICIPANT_ROLES = [UserRoleBillingUserType.FACILITATOR, UserRoleBillingUserType.MANAGER],

    getDefaultStrategyQuery: StoreExtractor<Query> = (_params, store) => convertStrategyFilterToQuery(store.strategy.strategyFilter, true),
    getDefaultStrategyExecutionQuery: StoreExtractor<Query> = (_params, store) => convertStrategyFilterToQuery(store.strategy.strategyExecutionFilter, true),

    getAlertAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) =>
        store.app.session.UserID && store.app.session.HasTemporaryAlertAccess ?
            RouteAccessType.READONLY :
            accessFromSession(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS)(params, store, dispatch),

    getIndicatorMapAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
        const { session } = store.app;

        if (session.Custom.status === RequestStatus.SUCCEEDED && session.Is4SCompany) {
            return accessFromPackage({
                functionality: AccessFunctionality.STRATEGY_WORK,
                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
            })(params, store, dispatch);
        }
        else {
            return RouteAccessType.HIDDEN;
        }
    },

    getStrategySettingsAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
        const { session } = store.app;

        if (session.Custom.status === RequestStatus.SUCCEEDED && (
            session.UseUserRoleBasedBilling ? session.UserRoleBillingUserTypeID === UserRoleBillingUserType.FACILITATOR : session.IsStrategyFacilitator
        )) {
            return accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch);
        }
        else {
            return RouteAccessType.HIDDEN;
        }
    },

    getWorkshopSessionAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
        const
            { app: { session } } = store,
            hasTemporaryAccess = session.UserID && session.HasTemporaryWorkshopAccess;

        if (hasTemporaryAccess) { return RouteAccessType.READONLY; }

        if (session.IsExternalUser) {
            const f = (session as unknown as entities.Session).FunctionalityAccesses.find((f: any) => f.FunctionalityID === AccessFunctionality.STRATEGY_WORK);
            switch (true) {
                case !session.UserID:
                    return UNKNOWN;
                case !f:
                    return RouteAccessType.HIDDEN;
                case f!.AccessTypeID === AccessType.NO_ACCESS:
                    return RouteAccessType.DISABLED;
                case f!.AccessTypeID === AccessType.READ:
                    return RouteAccessType.READONLY;
                default:
                    return RouteAccessType.FULL;
            }
        }

        return accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch);
    },

    getManagementCalendarAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
        if (!store.app.session.IsUser4SAdministrator) { return RouteAccessType.HIDDEN; }
        return accessFromPackage({
            functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
            [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
            [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
            [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
        })(params, store, dispatch);
    },

    getIndicatorAndTrackingLevelsAccess: StoreExtractor<RouteAccessType> = (params, store, dispatch) => {
        const indicatorAccess = accessFromUserRole(AccessFunctionality.INDICATOR_MANAGEMENT, ...NON_PARTICIPANT_ROLES)(params, store, dispatch);
        if (store.app.session.ProductPackageID !== ProductPackage.SMART_STRATEGY) { return indicatorAccess; }

        const dataSourceAccess = accessFromSession(AccessFunctionality.DATA_SOURCES)(params, store, dispatch);

        if (indicatorAccess === RouteAccessType.HIDDEN || dataSourceAccess === RouteAccessType.HIDDEN) { return RouteAccessType.HIDDEN; }
        if (indicatorAccess === RouteAccessType.DISABLED || dataSourceAccess === RouteAccessType.DISABLED) { return RouteAccessType.DISABLED; }
        if (indicatorAccess === RouteAccessType.READONLY || dataSourceAccess === RouteAccessType.READONLY) { return RouteAccessType.READONLY; }

        return RouteAccessType.FULL;
    },

    DEFAULT_COMPANY_TEMPLATE_TYPE = 1,
    ADVISORY_TEMPLATE_TYPE = 4;


// just to check type of every plain object and get proper line numbers in error messages
// TODO is there a proper way to get detailed errors about such object in TypeScript rather than "Type {...} is not assignable"?
// call buildRuntimeRoutes per route rather than recursively? it will also check for checkedness itself and avoid useless runtime calls

function c(r: ConfigRedirectRoute): ConfigRoute;
function c(r: ConfigAutoRedirectRoute): ConfigRedirectRoute;
function c(r: ConfigComponentRoute): ConfigRoute;
function c(r: ConfigRoute): ConfigRoute {
    return r;
}

const
    overrideSmartStrategyAccess = (extractor: StoreExtractor<RouteAccessType>, showInSmartStrategy: boolean): StoreExtractor<RouteAccessType> =>
        (params, store, dispatch) => {
            const isSmartStrategyPackage = store.app.session.ProductPackageID === ProductPackage.SMART_STRATEGY;

            if (isSmartStrategyPackage && showInSmartStrategy) {
                return RouteAccessType.FULL;
            }
            else if (!isSmartStrategyPackage && !showInSmartStrategy) {
                return extractor(params, store, dispatch);
            }

            return RouteAccessType.HIDDEN;
        },
    getTableAutomationRoutes = (rootPath: string, showInSmartStrategy: boolean, menuOptions: MenuOptions | undefined = undefined) => c({
        path: `${rootPath}/table_automation`,
        title: { id: 'navigation.table_automation' },
        menuOptions,
        redirect: `${rootPath}/table_automation/tables`,

        childRoutes: [
            c({
                path: `${rootPath}/table_automation/tables`,
                access: overrideSmartStrategyAccess(accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES), showInSmartStrategy),
                title: { id: 'navigation.tables' },
                menuOptions: { hidden: true },
                component: <ComponentLoader scope="integration" component="TablesAndAuthorizations" />,

                childRoutes: [
                    c({
                        path: `${rootPath}/table_automation/tables/:sourceID`,
                        access: overrideSmartStrategyAccess(accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES), showInSmartStrategy),
                        title: fromStore('integrations.integrationSources.Data.:sourceID.Description', { id: 'integration.untitled_integration' }),
                        component: <ComponentLoader scope="integration" component="TablesAndAuthorizations" dlls={['highcharts']} />,

                        childRoutes: [
                            c({
                                path: `${rootPath}/table_automation/tables/:sourceID/:sourceItemID`,
                                access: overrideSmartStrategyAccess(accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES), showInSmartStrategy),
                                title: (params, store, dispatch) => {
                                    if ((store.integrations as any).integrationSources.Custom.status === RequestStatus.UNSYNCED) {
                                        dispatch(untypedActions.loadAllIntegrationSources());
                                    }

                                    return fromStore('integrations.integrationSources.Data.:sourceID.Description', { id: 'integration.untitled_integration' })(params, store, dispatch);
                                },
                                menuOptions: { hidden: true },
                                component: <ComponentLoader scope="integration" component="TablesAndAuthorizations" dlls={['highcharts']} />
                            })
                        ]
                    })
                ]
            }),
            c({
                path: `${rootPath}/table_automation/authorizations`,
                access: overrideSmartStrategyAccess(accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES), showInSmartStrategy),
                title: { id: 'navigation.authorizations' },
                menuOptions: { hidden: true },
                component: <ComponentLoader scope="integration" component="TablesAndAuthorizations" showAuthorizations />
            }),
            c({
                path: `${rootPath}/table_automation/cashflow_settings`,
                access: (params, store, dispatch) => store.app.session.CashflowForecastEnabled ?
                    overrideSmartStrategyAccess(accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES), showInSmartStrategy)(params, store, dispatch) :
                    RouteAccessType.HIDDEN,
                title: { id: 'navigation.cashflow_settings' },
                menuOptions: { hidden: true },
                component: <ComponentLoader scope="integration" component="CashflowForecastSettings" />
            })
        ]
    }),

    buildRedirects = (map: { [from: string]: string; }): ConfigRedirectRoute[] => Object.keys(map).map(from => ({ path: from, redirect: map[from] })),

    publicPageRoutes = buildRuntimeRoutes([
        c({
            access: RouteAccessType.READONLY,
            title: { id: 'navigation.home' },
            component: Root as ComponentClass,
            matchChildRoutes: true,
            childRoutes: [
                c({
                    path: '/public/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})',
                    title: { id: 'navigation.home' },
                    access: RouteAccessType.READONLY,
                    menuOptions: { hidden: true },
                    component: <ComponentLoader scope="public" component="Public" dlls={['gojs', 'highcharts']} />,

                    childRoutes: [
                        c({
                            path: '/public/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/:secondaryEntityID',
                            title: { id: 'navigation.home' },
                            access: RouteAccessType.READONLY,
                            menuOptions: { hidden: true },
                            component: <ComponentLoader scope="public" component="Public" dlls={['gojs', 'highcharts']} />
                        })
                    ]
                })
            ]
        })
    ]),

    allRoutes = buildRuntimeRoutes([
        c({
            access: RouteAccessType.FULL,
            title: { id: 'navigation.home' },
            component: Root as ComponentClass,
            matchChildRoutes: true,
            childRoutes: [
                c({
                    title: 'Special layout routes',
                    menuOptions: { hidden: true },
                    redirect: '/agenda360',

                    childRoutes: [
                        c({
                            path: '/public/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})',
                            title: { id: 'navigation.home' },
                            access: RouteAccessType.READONLY,
                            menuOptions: { hidden: true },
                            component: <ComponentLoader scope="public" component="Public" />,

                            childRoutes: [
                                c({
                                    path: '/public/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/:secondaryEntityID',
                                    title: { id: 'navigation.home' },
                                    access: RouteAccessType.READONLY,
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="public" component="Public" />
                                })
                            ]
                        }),
                        c({
                            path: '/inactive_company',
                            access: RouteAccessType.FULL,
                            title: fromStore<Resource>('app.session.CurrentCompanyName'),
                            component: <ComponentLoader scope="layout" component="FaultyCompanyRedirection" />
                        }),
                        c({
                            path: '/welcome_guide',
                            access: RouteAccessType.FULL,
                            title: { id: 'navigation.welcome_guide' },
                            matchChildRoutes: true,
                            component: <ComponentLoader scope="welcome-guide" component="WelcomeGuideSlideContainer" />,
                            onEnter: ((state, replace) => {
                                if (state.location.pathname === '/welcome_guide') {
                                    replace('/welcome_guide/introduction');
                                }
                            }) as EnterHook,

                            childRoutes: [
                                c({
                                    path: '/welcome_guide/introduction',
                                    access: RouteAccessType.FULL,
                                    title: { id: 'welcome_guide.introduction' },
                                    component: <ComponentLoader scope="welcome-guide" component="IntroductionSlide" />
                                }),
                                c({
                                    path: '/welcome_guide/password',
                                    access: RouteAccessType.FULL,
                                    title: { id: 'welcome_guide.password' },
                                    component: <ComponentLoader scope="welcome-guide" component="PasswordSlide" />
                                }),
                                c({
                                    path: '/welcome_guide/profile',
                                    access: RouteAccessType.FULL,
                                    title: { id: 'welcome_guide.profile' },
                                    component: <ComponentLoader scope="welcome-guide" component="ProfileSlide" />
                                })
                                /* c({
                                    path: '/welcome_guide/mobile',
                                    access: RouteAccessType.FULL,
                                    title: { id: 'welcome_guide.mobile' },
                                    component: <ComponentLoader scope="welcome-guide" component="MobileSlide" />
                                }) */
                            ]
                        }),
                        c({
                            path: '/alerts/:alertID/preview',
                            access: getAlertAccess,
                            title: fromStore<Resource>('dashboards.pages.Data.:alertID.Name', { id: 'performance_alert.untitled_alert' }),
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" />
                        }),
                        c({
                            path: '/alerts/:alertID/review',
                            access: getAlertAccess,
                            title: fromStore<Resource>('dashboards.pages.Data.:alertID.Name', { id: 'performance_alert.untitled_alert' }),
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" review />
                        }),
                        c({
                            path: '/alerts/:alertID/:batchID',
                            access: getAlertAccess,
                            title: { id: 'performance_alert.untitled_alert' },
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" />
                        }),
                        c({
                            path: '/alerts/:alertID/:batchID/review',
                            access: getAlertAccess,
                            title: { id: 'performance_alert.untitled_alert' },
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" review />
                        }),
                        c({
                            path: '/alerts/:alertID',
                            access: getAlertAccess,
                            title: fromStore<Resource>('dashboards.pages.Data.:alertID.Name', { id: 'performance_alert.untitled_alert' }),
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" />
                        }),
                        c({
                            path: '/scorecards/information_sharing/performance_alerts/:alertID/designer',
                            access: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                            title: { id: 'navigation.performance_alert_settings' },
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlert" editing />
                        }),
                        c({
                            path: `/data_management/table_automation/forecasts/new/:modelID/:levelID/:entryID/${ForecastModelDataEntryType.GOOGLE_SHEET}`,
                            access: AccessFunctionality.FORECASTS,
                            title: fromStore('forecasts.forecastModels.Data.:modelID.Levels.:levelID.Entries.:entryID.Name', { id: 'integration.untitled_file' }),
                            component: <ComponentLoader scope="integration" component="ForecastModelGoogleSheet" />
                        }),
                        ...(Object.values(MeetingPresentationDataSource) as unknown as number[]).map(sourceID => c({
                            path: `/agenda360/meetings/:companyFunctionID/${sourceID}/:meetingID`,
                            access: AccessFunctionality.AGENDA_AND_ACTIONS,
                            title: fromStore<Resource>(`meeting.${MEETING_PRESENTATION_DATA_SOURCE_STORE_KEYS_MAP[sourceID]}.Data.:meetingID.Title`),
                            component: <ComponentLoader component="MeetingPresentation" dlls={['highcharts']} scope="meeting" sourceID={sourceID} />,

                            childRoutes: [
                                c({
                                    path: `/agenda360/meetings/:companyFunctionID/${sourceID}/:meetingID/:sectionID`,
                                    access: AccessFunctionality.AGENDA_AND_ACTIONS,
                                    title: fromStore<Resource>(`meeting.${MEETING_PRESENTATION_DATA_SOURCE_STORE_KEYS_MAP[sourceID]}.Data.:meetingID.Title`),
                                    component: <ComponentLoader component="MeetingPresentation" dlls={['highcharts']} scope="meeting" sourceID={sourceID} />
                                })
                            ]
                        })),
                        c({
                            path: '/strategy/analyses/workshops/workshop',
                            access: (params, store, dispatch) => {
                                const { session } = store.app;

                                if (session.Custom.status === RequestStatus.SUCCEEDED && session.Is4SCompany) {
                                    return accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch);
                                }
                                else {
                                    return RouteAccessType.HIDDEN;
                                }
                            },
                            title: { id: 'navigation.workshops' },
                            beta: true,
                            component: <ComponentLoader scope="strategy-sprint" component="Workshop" />
                        }),
                        c({
                            path: '/workshops/:workshopID',
                            access: getWorkshopSessionAccess,
                            title: fromStore('workshop.workshops.Data.:workshopID.Name', { id: 'navigation.workshop' }),
                            component: <ComponentLoader scope="workshop" component="WorkshopSession" dlls={['gojs', 'highcharts']} />,

                            childRoutes: [
                                c({
                                    path: '/workshops/:workshopID/:phaseID',
                                    access: getWorkshopSessionAccess,
                                    title: fromStore('workshop.workshops.Data.:workshopID.Name', { id: 'navigation.workshop' }),
                                    component: <ComponentLoader scope="workshop" component="WorkshopSession" dlls={['gojs', 'highcharts']} />
                                })
                            ]
                        }),
                        c({
                            path: '/mindmaps/:mindmapID',
                            access: AccessFunctionality.STRATEGY_WORK,
                            title: fromStore('workshop.workspaceMindmaps.Data.:mindmapID.Name', { id: 'navigation.mindmap' }),
                            component: <ComponentLoader scope="workshop" component="HierarchicalMindmap" dlls={['gojs']} />
                        }),
                        c({
                            path: '/strategy/strategy_overview/:strategyID',
                            access: AccessFunctionality.STRATEGY_WORK,
                            title: { id: 'navigation.strategy_snapshot' },
                            beta: true,
                            menuOptions: { hidden: true },
                            component: <ComponentLoader scope="strategy" component="StrategyExecutionOverview" dlls={['highcharts']} />
                        }),
                        c({
                            path: '/strategy/status_updates',
                            access: AccessFunctionality.STRATEGY_WORK,
                            title: '',
                            component: <ComponentLoader scope="strategy" component="StrategyStatusUpdater" dlls={['highcharts', 'gojs']} />
                        }),
                        c({
                            path: '/dialog_flow/sessions/:sessionID',
                            access: AccessFunctionality.STRATEGY_WORK,
                            title: '',
                            component: <ComponentLoader scope="dialog" component="DialogSessionRunner" dlls={['highcharts', 'gojs']} />
                        }),
                        c({
                            path: '/dialog_flow/templates/:templateID',
                            access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, UserRoleBillingUserType.FACILITATOR),
                            title: '',
                            component: <ComponentLoader scope="dialog" component="DialogFlowTemplateEditor" dlls={['gojs']} />
                        })
                    ]
                }),
                c({
                    access: RouteAccessType.FULL,
                    title: { id: 'navigation.home' },
                    matchChildRoutes: true,
                    menuOptions: { root: true },
                    component: <ComponentLoader scope="layout" component="Layout" />,

                    childRoutes: [
                        c({
                            path: '/agenda360',
                            access: accessFromPackage({
                                functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.AGENDA_HOME)
                            }),
                            title: { id: 'navigation.agenda_360' },
                            matchChildRoutes: true,
                            menuOptions: { icon: 'home' },
                            component: <ComponentLoader scope="meeting" component="AgendaRoot" dlls={['gojs', 'highcharts']} />,

                            childRoutes: [
                                c({
                                    path: '/agenda360/insight_feed',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.AGENDA_HOME)
                                    }),
                                    title: { id: 'navigation.pulse' },
                                    component: <ComponentLoader scope="insight" component="InsightFeed" dlls={['gojs', 'highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/agenda360/insight_feed/:companyFunctionID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.AGENDA_HOME)
                                            }),
                                            title: { id: 'navigation.pulse' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="insight" component="InsightFeed" dlls={['gojs', 'highcharts']} />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/agenda360/activities',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.activities' },
                                    component: <ComponentLoader scope="meeting" component="AgendaActivities" dlls={['gojs', 'highcharts']} />
                                }),
                                c({
                                    path: '/agenda360/calendar',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.meetings' },
                                    component: <ComponentLoader scope="calendar" component="Calendar" useAgendaFilter />
                                }),
                                c({
                                    path: '/agenda360/management_calendar',
                                    access: getManagementCalendarAccess,
                                    title: { id: 'navigation.management_system' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="calendar" component="ManagementCalendar" />
                                }),
                                c({
                                    path: '/agenda360/newsroom',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.NEWS,
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.newsroom' },
                                    menuOptions: {
                                        hidden: true,
                                        query: getDefaultNewsroomQuery
                                    },
                                    component: <ComponentLoader scope="newsfeed" component="ApprovalFeed" inAgendaContext />,
                                    onEnter: ((state, replace) => {
                                        const query = getDefaultNewsroomQuery(state.params, store.getState(), store.dispatch);
                                        if (!Object.keys(state.location.query).length && query !== UNKNOWN) {
                                            replace({ pathname: state.location.pathname, query });
                                        }
                                    }) as EnterHook,

                                    childRoutes: [
                                        c({
                                            path: '/agenda360/newsroom/:companyFunctionID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.NEWS,
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: { id: 'navigation.newsroom' },
                                            menuOptions: {
                                                icon: 'newspaper-o',
                                                hidden: true,
                                                query: getDefaultNewsroomQuery
                                            },
                                            component: <ComponentLoader scope="newsfeed" component="ApprovalFeed" inAgendaContext />,
                                            onEnter: ((state, replace) => {
                                                const query = getDefaultNewsroomQuery(state.params, store.getState(), store.dispatch);
                                                if (!Object.keys(state.location.query).length && query !== UNKNOWN) {
                                                    replace({ pathname: state.location.pathname, query });
                                                }
                                            }) as EnterHook
                                        })
                                    ]
                                }),
                                c({
                                    path: '/agenda360/initiatives',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.initiatives' },
                                    menuOptions: { hidden: (_params, store) => !(store.app as any).session.CompanyHasInitiatives },
                                    component: <ComponentLoader component="ManagementAgenda" scope="meeting" dlls={['highcharts']} filterByTeam />,

                                    childRoutes: [
                                        c({
                                            path: '/agenda360/initiatives/:companyFunctionID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: { id: 'navigation.initiatives' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader component="ManagementAgenda" scope="meeting" dlls={['highcharts']} filterByTeam />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/agenda360/workshops',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.group_collaboration' },
                                    component: <ComponentLoader
                                        scope="workshop"
                                        component="GroupCollaboration"
                                        statuses={[GroupCollaborationEntityStatus.ACTIVE, GroupCollaborationEntityStatus.COMPLETED]}
                                        owner={GroupCollaborationEntityOwner.PERSONAL}
                                        useAgendaFilter
                                        showPageControls={false}
                                    />
                                }),
                                c({
                                    path: '/agenda360/business_review',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.business_review' },
                                    beta: true,
                                     component: <ComponentLoader scope="business-review" component="BusinessReview" />,
                                    menuOptions: { query: getDefaultStrategyExecutionQuery, hidden: true },

                                    childRoutes: [
                                        c({
                                            path: '/agenda360/business_review/:companyFunctionID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: { id: 'navigation.business_review' },
                                            beta: true,
                                            menuOptions: { query: getDefaultStrategyExecutionQuery, hidden: true },
                                            component: <ComponentLoader scope="business-review" component="BusinessReview" />
                                        })
                                    ]
                                })
                            ]
                        }),

                        c({
                            path: '/calendar',
                            title: { id: 'navigation.meetings' },
                            menuOptions: { icon: 'user-pair' },
                            redirect: '/calendar/meetings',

                            childRoutes: [
                                c({
                                    path: '/calendar/meetings',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.meetings' },
                                    component: <ComponentLoader scope="calendar" component="Calendar" />,

                                    childRoutes: [
                                        c({
                                            path: '/calendar/meetings/:companyFunctionID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: { id: 'navigation.meetings' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="calendar" component="Calendar" />,

                                            childRoutes: (Object.values(MeetingPresentationDataSource) as number[]).map(sourceID => c({
                                                path: `/agenda360/meetings/:companyFunctionID/${sourceID}/:meetingID`,
                                                access: accessFromPackage({
                                                    functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                    [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                                    [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                                    [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                }),
                                                title: fromStore(`meeting.${MEETING_PRESENTATION_DATA_SOURCE_STORE_KEYS_MAP[sourceID]}.Data.:meetingID.Title`),
                                                component: <ComponentLoader component="MeetingPresentation" dlls={['highcharts']} scope="meeting" sourceID={sourceID} />,

                                                childRoutes: [
                                                    c({
                                                        path: `/agenda360/meetings/:companyFunctionID/${sourceID}/:meetingID/:sectionID`,
                                                        access: accessFromPackage({
                                                            functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                                            [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                                            [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                                            [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                        }),
                                                        title: fromStore(`meeting.${MEETING_PRESENTATION_DATA_SOURCE_STORE_KEYS_MAP[sourceID]}.Data.:meetingID.Title`),
                                                        component: <ComponentLoader component="MeetingPresentation" dlls={['highcharts']} scope="meeting" sourceID={sourceID} />
                                                    })
                                                ]
                                            }))
                                        })
                                    ]
                                }),

                                c({
                                    path: '/calendar/meeting_templates',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.meeting_templates' },
                                    component: <ComponentLoader scope="meeting" component="MeetingOverview" />
                                }),

                                c({
                                    path: '/calendar/tasks',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.tasks' },
                                    component: <ComponentLoader scope="meeting" component="DecisionTable" showDecisions={false} showTaskFilter />
                                }),

                                c({
                                    path: '/calendar/decisions',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.AGENDA_AND_ACTIONS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.AGENDA_AND_ACTIONS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.AGENDA_AND_ACTIONS, AccessFunctionalityAddOn.MEETINGS),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.decisions' },
                                    component: <ComponentLoader scope="meeting" component="DecisionTable" showTasks={false} showDecisionFilter />
                                })
                            ]
                        }),

                        c({
                            path: '/group_collaboration',
                            title: { id: 'navigation.group_collaboration' },
                            menuOptions: { icon: 'user-screen' },
                            redirect: '/group_collaboration/ongoing',

                            childRoutes: [
                                c({
                                    path: '/group_collaboration/ongoing',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.CO_CREATION),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.ongoing' },
                                    component: <ComponentLoader scope="workshop" component="GroupCollaboration" statuses={[GroupCollaborationEntityStatus.ACTIVE]} />
                                }),
                                c({
                                    path: '/group_collaboration/completed',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.CO_CREATION),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.completed' },
                                    component: <ComponentLoader scope="workshop" component="GroupCollaboration" statuses={[GroupCollaborationEntityStatus.COMPLETED]} />
                                }),
                                c({
                                    path: '/group_collaboration/templates',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.CO_CREATION),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.templates' },
                                    component: <ComponentLoader
                                        scope="workshop"
                                        component="GroupCollaboration"
                                        statuses={[GroupCollaborationEntityStatus.TEMPLATE]}
                                        owner={GroupCollaborationEntityOwner.ALL}
                                    />
                                }),
                                c({
                                    path: '/group_collaboration/schedules/:scheduleID',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.CO_CREATION),
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: fromStore('dialog.dialogScheduleSettings.Data.:dialogScheduleID.Name'),
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader
                                        scope="workshop"
                                        component="GroupCollaboration"
                                        entityType={GroupCollaborationEntityType.DIALOG}
                                        statuses={[GroupCollaborationEntityStatus.ACTIVE]}
                                        owner={GroupCollaborationEntityOwner.ALL}
                                    />
                                })
                            ]
                        }),

                        c({
                            path: '/strategy',
                            access: accessFromPackage({
                                functionality: AccessFunctionality.STRATEGY_WORK,
                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                            }),
                            title: { id: 'navigation.strategy_builder' },
                            matchChildRoutes: true,
                            menuOptions: { icon: 'chess-knight', query: getDefaultStrategyQuery },
                            component: <ComponentLoader scope="strategy" component="StrategyRoot" dlls={['gojs', 'highcharts']} />,

                            childRoutes: [
                                c({
                                    path: '/strategy/strategy_map',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.map' },
                                    menuOptions: { query: getDefaultStrategyQuery, hidePadding: true },
                                    component: <ComponentLoader scope="strategy" component="StrategyPlanner" dlls={['gojs', 'highcharts']} />
                                }),
                                c({
                                    path: '/strategy/strategy_description',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.strategy_description' },
                                    menuOptions: {
                                        query: (_params: Params, store: Store) => {
                                            const query = convertStrategyFilterToQuery(store.strategy.strategyFilter);
                                            return { ...query, strategyIDs: query.strategyIDs?.length ? [query.strategyIDs.first()!] : [] };
                                        }
                                    },
                                    component: <ComponentLoader scope="strategy" component="StrategyDescription" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/strategy/big_picture',
                                    access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.strategy_big_picture' },
                                    beta: true,
                                    menuOptions: { hidden: true, query: getDefaultStrategyQuery, hidePadding: true },
                                    component: <ComponentLoader scope="strategy" component="StrategyBigPicture" dlls={['gojs', 'highcharts']} />
                                }),
                                c({
                                    path: '/strategy/strategy_modelling/publishing/:strategyID',
                                    access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.review_and_publish' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="strategy" component="StrategyPublishing" />
                                }),
                                c({
                                    path: '/strategy/indicators',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.kpis' },
                                    menuOptions: { query: getDefaultStrategyQuery },
                                    component: <ComponentLoader scope="strategy" component="StrategyIndicators" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/strategy/analyses/market_analyses',
                                    access: accessFromUserRole(AccessFunctionality.MARKET_ANALYSES, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.market_analyses' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="stakeholders" component="MarketAnalysesList" dlls={['highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/strategy/analyses/market_analyses/analysis',
                                            access: accessFromUserRole(AccessFunctionality.MARKET_ANALYSES, ...NON_PARTICIPANT_ROLES),
                                            title: (_params, store, _dispatch) => {
                                                const location = store.routing.locationBeforeTransitions;

                                                if (location.query.viewID) {
                                                    const view = store.stakeholders.stakeholderMarketPositioningMapViews.Data.getByID(+location.query.viewID);
                                                    if (view) {
                                                        return view.Name;
                                                    }
                                                }

                                                if (location.query.benchmarkID) {
                                                    const benchmark = store.stakeholders.stakeholderBenchmarks.Data.getByID(+location.query.benchmarkID);
                                                    if (benchmark) {
                                                        return benchmark.Name;
                                                    }
                                                }

                                                return UNKNOWN;
                                            },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="stakeholders" component="StakeholderGroup" dlls={['highcharts']} />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/strategy/analyses/swot',
                                    access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.swot' },
                                    menuOptions: {
                                        icon: 'swot-analysis',
                                        hidden: true
                                    },
                                    component: <ComponentLoader scope="swot" component="SWOTSessionList" />,

                                    childRoutes: [
                                        c({
                                            path: '/strategy/analyses/swot/:sessionID',
                                            access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                            title: fromStore('swot.sessions.Data.:sessionID.Name', { id: 'swot.untitled_session' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="swot" component="SWOTSession" dlls={['highcharts']} />,

                                            childRoutes: [
                                                c({
                                                    path: '/strategy/analyses/swot/:sessionID/topics',
                                                    access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                                    title: { id: 'topics.topics' },
                                                    component: <ComponentLoader scope="swot" component="SWOTTopicList" />
                                                }),
                                                c({
                                                    path: '/strategy/analyses/swot/:sessionID/:areaID',
                                                    access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                                    title: fromStore('swot.areas.Data.:areaID.Description'),
                                                    component: <ComponentLoader scope="swot" component="SWOTTopicList" />,

                                                    childRoutes: [
                                                        c({
                                                            path: '/strategy/analyses/swot/:sessionID/:areaID/:topicID',
                                                            access: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                                            title: fromStore('swot.sessions.Data.:sessionID.Topics.:topicID.Name', { id: 'swot.untitled_topic' }),
                                                            component: <ComponentLoader scope="swot" component="SWOTTopicList" />
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                // c({
                                //     path: '/strategy/strategy_process_management',
                                //     access: AccessFunctionality.STRATEGY_WORK,
                                //     title: { id: 'navigation.strategy_process_management' },
                                //     menuOptions: { icon: 'globe-alt' },
                                //     component: <ComponentLoader scope="strategy" component="StrategyProcessManagement" dlls={['gojs']} />,

                                //     childRoutes: [
                                //         c({
                                //             path: '/strategy/strategy_process_management/:strategyProcessID',
                                //             access: AccessFunctionality.STRATEGY_WORK,
                                //             title: { id: 'navigation.strategy_process_management' },
                                //             menuOptions: { hidden: true },
                                //             component: <ComponentLoader scope="strategy" component="StrategyProcessManagement" dlls={['gojs']} />
                                //         })
                                //     ]
                                // }),
                                /* c({
                                    path: '/strategy/strategy_execution/onboarding',
                                    access: AccessFunctionality.STRATEGY_WORK,
                                    title: { id: 'navigation.strategy' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="strategy" component="StrategyOnboarding" />
                                }),*/
                                c({
                                    path: '/strategy/strategy_overview',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.strategy_snapshot' },
                                    menuOptions: { hidden: true },
                                    beta: true,
                                    component: <ComponentLoader scope="strategy" component="StrategyExecutionOverview" />
                                }),
                                c({
                                    path: '/strategy/strategy_modelling/business_models',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.business_models' },
                                    menuOptions: {
                                        hidden: true,
                                        icon: 'cogs'
                                    },
                                    beta: true,
                                    component: <ComponentLoader scope="strategy" component="BusinessModelList" />,

                                    childRoutes: [
                                        c({
                                            path: '/strategy/strategy_modelling/business_models/:modelID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.STRATEGY_WORK,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('strategy.businessModels.Data.:modelID.Name', { id: 'business_models.untitled_model' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="strategy" component="BusinessModel" />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/strategy/strategy_execution/snapshot',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.snapshot' },
                                    menuOptions: {
                                        hidden: true
                                    },
                                    component: <ComponentLoader scope="strategy" component="StrategySummary" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/strategy/strategy_execution/pending_actions',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.STRATEGY_WORK, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.pending_actions' },
                                    menuOptions: {
                                        hidden: true
                                    },
                                    component: <ComponentLoader scope="strategy" component="StrategyExecution" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/strategy/strategy_execution/indicator_map',
                                    access: getIndicatorMapAccess,
                                    title: { id: 'navigation.indicator_map' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader component="IndicatorMap" scope="performance-indicator" dlls={['gojs']} />
                                })
                            ]
                        }),

                        c({
                            path: '/strategy_execution',
                            access: accessFromPackage({
                                functionality: AccessFunctionality.STRATEGY_WORK,
                                [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                            }),
                            title: { id: 'navigation.strategy_execution' },
                            matchChildRoutes: true,
                            menuOptions: { icon: 'bullseye-arrow', query: getDefaultStrategyExecutionQuery },
                            component: <ComponentLoader scope="strategy" component="StrategyRoot" useExecutionFilter dlls={['gojs', 'highcharts']} />,

                            childRoutes: [
                                c({
                                    path: '/strategy_execution/roadmap',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.roadmap' },
                                    menuOptions: { query: getDefaultStrategyExecutionQuery },
                                    component: <ComponentLoader scope="strategy" component="StrategyRoadmap" dlls={['gojs', 'highcharts']} />
                                }),
                                c({
                                    path: '/strategy_execution/strategy_board',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.board' },
                                    menuOptions: { query: getDefaultStrategyExecutionQuery },
                                    component: <ComponentLoader scope="strategy" component="StrategyKanban" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/strategy_execution/organization_structure',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.SMART_STRATEGY]: accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.TEAM_MANAGEMENT),
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.team_map' },
                                    menuOptions: { query: getDefaultStrategyExecutionQuery, hidePadding: true },
                                    component: <ComponentLoader scope="strategy" component="StrategyOrganizationStructure" dlls={['gojs', 'highcharts']} />
                                }),
                                c({
                                    path: '/strategy_execution/execution_review',
                                    access: (params, store, dispatch) => {
                                        const { session } = store.app;
                                        if (session.ProductPackageID === ProductPackage.GOALWISOR) { return RouteAccessType.HIDDEN; }

                                        const defaultAccessType = session.ProductPackageID !== ProductPackage.SMART_STRATEGY ?
                                            accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch) :
                                            accessFromSession(AccessFunctionality.STRATEGY_WORK, AccessFunctionalityAddOn.EXECUTION_REVIEW)(params, store, dispatch);

                                        if (defaultAccessType !== RouteAccessType.FULL) { return defaultAccessType; }

                                        let isAdministrator = false;

                                        if (session.UseUserRoleBasedBilling) {
                                            isAdministrator = session.UserRoleBillingUserTypeID === UserRoleBillingUserType.FACILITATOR ||
                                                session.UserRoleBillingUserTypeID === UserRoleBillingUserType.MANAGER;
                                        }
                                        else {
                                            isAdministrator = session.IsUserAdministrator || session.IsStrategyFacilitator;
                                        }

                                        return isAdministrator ? RouteAccessType.FULL : RouteAccessType.READONLY;
                                    },
                                    title: { id: 'navigation.execution_review' },
                                    menuOptions: {
                                        query: getDefaultStrategyExecutionQuery,
                                        hidePadding: true
                                    },
                                    component: <ComponentLoader scope="strategy" component="BusinessReview" />
                                }),
                                c({
                                    path: '/strategy_execution/action_feedback',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.STRATEGY_WORK,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.action_feedback' },
                                    beta: (_params: Params, store: Store, _dispatch: Dispatch) => store.app.session.ProductPackageID !== ProductPackage.SMART_STRATEGY,
                                    menuOptions: { query: getDefaultStrategyExecutionQuery },
                                    component: <ComponentLoader scope="strategy" component="ActionFeedback" />,

                                    childRoutes: [
                                        c({
                                            path: '/strategy_execution/action_feedback/:scheduleID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.STRATEGY_WORK,
                                                [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('dialog.dialogScheduleSettings.Data.:dialogScheduleID.Name'),
                                            beta: (_params: Params, store: Store, _dispatch: Dispatch) => store.app.session.ProductPackageID !== ProductPackage.SMART_STRATEGY,
                                            menuOptions: {
                                                hidden: true,
                                                query: getDefaultStrategyExecutionQuery
                                            },
                                            component: <ComponentLoader scope="strategy" component="ActionFeedback" />
                                        })
                                    ]
                                })
                            ]
                        }),

                        c({
                            path: '/scorecards',
                            title: (_params, store, dispatch) => {
                                if ((store.dashboards as unknown as any).dashboardLightGroups.Custom.status === RequestStatus.UNSYNCED) {
                                    dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                }

                                return { id: 'navigation.scorecards' };
                            },
                            menuOptions: { icon: 'bars-sort' },

                            childRoutes: [
								c({
                                    path: '/scorecards/indicators',
                                    access: (params, store, dispatch) =>
                                        store.app.session.ProductPackageID === ProductPackage.SMART_STRATEGY ||
                                        store.app.session.ProductPackageID === ProductPackage.GOALWISOR ?
                                            accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch) :
                                            RouteAccessType.HIDDEN,
                                    title: { id: 'navigation.goalwisor.kpis' },
                                    menuOptions: { query: getDefaultStrategyQuery },
                                    component: <ComponentLoader scope="strategy" component="StrategyIndicators" dlls={['highcharts']} filterOverride={DefaultStrategyFilter} />
                                }),
                                c({
                                    path: '/scorecards/homepage',
                                    access: (params, store, dispatch) =>
                                        store.app.session.ProductPackageID === ProductPackage.GOALWISOR ?
											RouteAccessType.HIDDEN :
                                            accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch),
                                    title: { id: 'navigation.homepage' },
                                    component: <ComponentLoader scope="dashboard" component="DashboardHomePage" dlls={['highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/scorecards/homepage/management',
                                            access: accessFromSession(USER_ADMINISTRATION),
                                            title: { id: 'navigation.home_page_configuration' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="dashboard" component="DashboardHomePageList" />
                                        })
                                    ]
                                }),

                                c({
                                    // in order to check if some route's subroute is active (to expand left menu groups, highlight links etc)
                                    // the child's path has to contain all the parameters used in parent's path
                                    // dashboards however were not designed to be grouped, but to be pointed directly, and there are plenty of existing links without ':groupID'
                                    // so here's some hack to make group menu items work properly
                                    path: '/business_insight/metrics/dashboards/:pageID',
                                    access: ((params, store, dispatch) =>
                                        accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) !== RouteAccessType.FULL ?
                                            accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) :
                                            accessFromSession(AccessFunctionality.INDICATOR_MANAGEMENT)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                RouteAccessType.READONLY :
                                                RouteAccessType.FULL) as StoreExtractor<RouteAccessType>,
                                    menuOptions: { hidden: true },
                                    // use 'title' to dispatch a replace action rather than 'redirect' or 'onEnter'
                                    // because it is always called while we are on the route whenever anything in store changes,
                                    // not just when the app enters the route
                                    // and is allowed to `dispatch`
                                    // so it can request missing data, wait for it, and redirect when data arrives
                                    title: ({ pageID }, store, dispatch) => {
                                        const groups: entities.NetworkArray<entities.DashboardGroupLightReport> = (store.dashboards as unknown as any).dashboardLightGroups;

                                        if (groups.Custom.status === RequestStatus.UNSYNCED) {
                                            dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                        }

                                        if (groups.Custom.status === RequestStatus.SUCCEEDED) {
                                            const page = groups.Data.map(({ Pages }) => Pages).flatten().getByID(+pageID);
                                            dispatch(untypedActions.replace({
                                                pathname: `/scorecards/dashboards/${page?.GroupID ?? 'uncategorized'}/${pageID}`,
                                                query: store.routing.locationBeforeTransitions.query
                                            }));

                                        }
                                        return UNKNOWN;
                                    },
                                    component: <Spinner />,

                                    childRoutes: [
                                        c({
                                            path: '/business_insight/metrics/dashboards/:pageID/:blockID',
                                            access: ((params, store, dispatch) =>
                                                accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                    accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) :
                                                    accessFromSession(AccessFunctionality.INDICATOR_MANAGEMENT)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                        RouteAccessType.READONLY :
                                                        RouteAccessType.FULL) as StoreExtractor<RouteAccessType>,
                                            title: ({ pageID, blockID }, store, dispatch) => {
                                                const groups: entities.NetworkArray<entities.DashboardGroupLightReport> = (store.dashboards as unknown as any).dashboardLightGroups;

                                                if (groups.Custom.status === RequestStatus.UNSYNCED) {
                                                    dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                                }

                                                if (groups.Custom.status === RequestStatus.SUCCEEDED) {
                                                    const page = groups.Data.map(({ Pages }) => Pages).flatten().getByID(+pageID);
                                                    dispatch(untypedActions.replace({
                                                        pathname: `/scorecards/dashboards/${page?.GroupID ?? 'uncategorized'}/${pageID}/${blockID}`,
                                                        query: store.routing.locationBeforeTransitions.query
                                                    }));

                                                }
                                                return UNKNOWN;
                                            },
                                            component: <Spinner />
                                        })
                                    ]
                                }),

                                c({
                                    path: '/scorecards/dashboards/:groupID',
                                    access: ((params, store, dispatch) =>
                                        accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) !== RouteAccessType.FULL ?
                                            accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) :
                                            accessFromSession(AccessFunctionality.INDICATOR_MANAGEMENT)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                RouteAccessType.READONLY :
                                                RouteAccessType.FULL) as StoreExtractor<RouteAccessType>,
                                    title: (params, store, dispatch) => {
                                        if ((store.dashboards as unknown as any).dashboardLightGroups.Custom.status === RequestStatus.UNSYNCED) {
                                            dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                        }

                                        const group = store.dashboards.dashboardLightGroups.Data.getByID(+params.groupID || 0);
                                        return group ? group.Name || { id: 'common.untitled_group' } : UNKNOWN;
                                    },
                                    menuOptions: {
                                        hidden: (params, store, _dispatch) => {
                                            const
                                                groups: entities.NetworkArray<entities.DashboardGroupLightReport> = store.dashboards.dashboardLightGroups,
                                                groupID = +params.groupID || 0,
                                                group = groups.Data.getByID(groupID),
                                                groupHasPages = groups.Custom.status === RequestStatus.SUCCEEDED && !!group?.Pages.length;

                                            if (groupHasPages) {
                                                return false;
                                            }

                                            const
                                                isUncategorizedGroup = !groupID,
                                                noGroupsWithPages = groups.Custom.status === RequestStatus.SUCCEEDED && !groups.Data.some(({ Pages }) => Pages.length);

                                            if (isUncategorizedGroup && noGroupsWithPages) {
                                                return false;
                                            }

                                            return true;
                                        },
                                        params: (_params, store, _dispatch) => {
                                            const groups: entities.NetworkArray<entities.DashboardGroupLightReport> = store.dashboards.dashboardLightGroups;
                                            return groups.Data.length || groups.Custom.status === RequestStatus.SUCCEEDED ?
                                                groups.Data.map(({ ID }) => ({ groupID: `${ID || 'uncategorized'}` })) :
                                                UNKNOWN;
                                        }
                                    },
                                    component: <ComponentLoader scope="dashboard" component="DashboardList" dlls={['highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/scorecards/dashboards/:groupID/:pageID',
                                            access: ((params, store, dispatch) =>
                                                accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                    accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) :
                                                    accessFromSession(AccessFunctionality.INDICATOR_MANAGEMENT)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                        RouteAccessType.READONLY :
                                                        RouteAccessType.FULL) as StoreExtractor<RouteAccessType>,
                                            title: (params, store, dispatch) => {
                                                if ((store.dashboards as unknown as any).dashboardLightGroups.Custom.status === RequestStatus.UNSYNCED) {
                                                    dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                                }

                                                const page: entities.DashboardPageLightReport | undefined = store.dashboards.dashboardLightGroups.Data
                                                    .map(({ Pages }) => Pages)
                                                    .flatten()
                                                    .getByID(+params.pageID);

                                                return page ? page.Name || { id: 'dashboards.untitled_page' } : UNKNOWN;
                                            },
                                            menuOptions: {
                                                collapsable: ({ pageID }, store, _dispatch) => {
                                                    const page: entities.DashboardPageLightReport | undefined = store.dashboards.dashboardLightGroups.Data
                                                        .map(({ Pages }) => Pages)
                                                        .flatten()
                                                        .getByID(+pageID);

                                                    if (!page) { return UNKNOWN; }

                                                    return page.Blocks.some(({ TypeID }) => (store.dashboards as any).blockTypes.Data.getByID(TypeID)?.Expandable);
                                                },
                                                params: ({ groupID }, store, _dispatch) => {
                                                    const
                                                        groups: entities.NetworkArray<entities.DashboardGroupLightReport> = store.dashboards.dashboardLightGroups,
                                                        group = groups.Data.getByID(+groupID || 0);

                                                    return group?.Pages.length || groups.Custom.status === RequestStatus.SUCCEEDED ?
                                                        (group?.Pages || []).map(({ ID }) => ({ pageID: `${ID}` })) :
                                                        UNKNOWN;
                                                }
                                            },
                                            component: <ComponentLoader scope="dashboard" component="DashboardPage" dlls={['highcharts']} />,

                                            childRoutes: [
                                                c({
                                                    path: '/scorecards/dashboards/:groupID/:pageID/:blockID',
                                                    access: (params: Params, store: Store, dispatch: Dispatch) =>
                                                        accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                            accessFromSession(AccessFunctionality.DASHBOARDS_AND_REPORTS)(params, store, dispatch) :
                                                            accessFromSession(AccessFunctionality.INDICATOR_MANAGEMENT)(params, store, dispatch) !== RouteAccessType.FULL ?
                                                                RouteAccessType.READONLY :
                                                                RouteAccessType.FULL,
                                                    title: (params, store, dispatch) => {
                                                        if ((store.dashboards as unknown as any).dashboardLightGroups.Custom.status === RequestStatus.UNSYNCED) {
                                                            dispatch(actions.loadAllLightDashboardGroups() as unknown as any);
                                                        }

                                                        if ((store.dashboards as unknown as any).blockTypes.Custom.status === RequestStatus.UNSYNCED) {
                                                            dispatch(untypedActions.loadBlockTypes());
                                                        }

                                                        const
                                                            blocks: entities.DashboardPageBlockLightReport[] = store.dashboards.dashboardLightGroups.Data
                                                                .map(({ Pages }) => Pages.map(({ Blocks }) => Blocks))
                                                                .flatten(),
                                                            block = blocks.getByID(+params.blockID);

                                                        return block ? block.Name || { id: 'dashboards.untitled_block' } : UNKNOWN;
                                                    },
                                                    menuOptions: {
                                                        params: ({ groupID, pageID }, store, _dispatch) => {
                                                            const
                                                                group = store.dashboards.dashboardLightGroups.Data.getByID(+groupID || 0),
                                                                page = group?.Pages.getByID(+pageID);

                                                            if (page) {
                                                                return Array
                                                                    .from({ length: page.Blocks.length }, (_, index) => index)
                                                                    .map((blockIndex) => {
                                                                        const block = page.Blocks[blockIndex];

                                                                        if (!block) { return { blockID: `${-(blockIndex + 1)}` }; }
                                                                        if (((store.dashboards as any).blockTypes.Data.getByID(block.TypeID) as unknown as any)?.Expandable) {
                                                                            return { blockID: `${block.ID}` };
                                                                        }

                                                                        return { blockID: '0' };
                                                                    })
                                                                    .filter(v => +v.blockID);
                                                            }

                                                            return UNKNOWN;
                                                        },
                                                        icon: ({ groupID, pageID, blockID }, store, _dispatch) => {
                                                            const
                                                                group: entities.DashboardGroupLightReport | undefined = store.dashboards.dashboardLightGroups.Data.getByID(+groupID || 0),
                                                                page: entities.DashboardPageLightReport | undefined = group?.Pages.getByID(+pageID),
                                                                block: entities.DashboardPageBlockLightReport | undefined = page?.Blocks.getByID(+blockID),
                                                                blockType: entities.DashboardPageBlockType | undefined = (store.dashboards as any).blockTypes.Data.getByID(block?.TypeID);

                                                            return blockType?.IconIdentifier || UNKNOWN;
                                                        }
                                                    },
                                                    component: <ComponentLoader scope="dashboard" component="DashboardBlock" dlls={['highcharts']} ignoreMaxDateRange />
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                getTableAutomationRoutes('/scorecards', true, { index: 4 }),
                                c({
                                    path: '/scorecards/workshops',
                                    access: (params: Params, store: Store, dispatch: Dispatch) =>
                                        store.app.session.ProductPackageID === ProductPackage.GOALWISOR ?
                                            accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch) :
                                            RouteAccessType.HIDDEN,
                                    title: { id: 'navigation.goalwisor.workshops' },
                                    menuOptions: { index: 6 },
                                    component: <ComponentLoader
                                        scope="workshop"
                                        component="GroupCollaboration"
                                        statuses={[GroupCollaborationEntityStatus.ACTIVE, GroupCollaborationEntityStatus.COMPLETED]}
                                    />,

                                    childRoutes: [
                                        c({
                                            path: '/scorecards/workshops/schedules/:scheduleID',
                                            access: (params, store, dispatch) =>
                                                store.app.session.ProductPackageID === ProductPackage.GOALWISOR ?
                                                    accessFromSession(AccessFunctionality.STRATEGY_WORK)(params, store, dispatch) :
                                                    RouteAccessType.HIDDEN,
                                            title: fromStore('dialog.dialogScheduleSettings.Data.:dialogScheduleID.Name'),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader
                                                scope="workshop"
                                                component="GroupCollaboration"
                                                entityType={GroupCollaborationEntityType.DIALOG}
                                                statuses={[GroupCollaborationEntityStatus.ACTIVE]}
                                                owner={GroupCollaborationEntityOwner.ALL}
                                            />
                                        })
                                    ]
                                })
                            ]
                        }),

                        c({
                            path: '/scenarios',
                            access: accessFromPackage({
                                functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                            }),
                            title: { id: 'navigation.financial_planning' },
                            menuOptions: { icon: 'atom-box' },
                            component: <ComponentLoader scope="foresights" component="ForesightScenario" dlls={['highcharts']} />,

                            childRoutes: [
                                c({
                                    path: '/scenarios/:scenarioID/cashflow',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.cashflow' },
                                    menuOptions: {
                                        params: (_params, store, _dispatch) => {
                                            const { scenarioID } = store.foresights;

                                            if (scenarioID) { return [{ scenarioID }]; }
                                            return [];
                                        }
                                    },
                                    component: <ComponentLoader scope="foresights" component="ForesightScenario" dlls={['highcharts']} view={ScenarioView.CASHFLOW} />
                                }),
                                c({
                                    path: '/scenarios/:scenarioID/summary',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.business_summary' },
                                    menuOptions: {
                                        params: (_params, store, _dispatch) => {
                                            const { scenarioID } = store.foresights;

                                            if (scenarioID) { return [{ scenarioID }]; }
                                            return [];
                                        }
                                    },
                                    component: <ComponentLoader scope="foresights" component="ForesightScenario" dlls={['highcharts']} view={ScenarioView.SUMMARY} />
                                }),
                                c({
                                    path: '/scenarios/:scenarioID/:levelID',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: ({ scenarioID, levelID }, store, _dispatch) => {
                                        const
                                            scenario: entities.ForesightScenarioReport | undefined = store.foresights.foresightScenarios.Data.getByID(+scenarioID),
                                            level: entities.ForesightScenarioLevelReport | undefined = scenario?.Levels.getByID(+levelID);

                                        return level ?
                                            getCorporateStructureLinkDescription(store.corporateStructure, level.CorporateStructureLink) || { id: 'navigation.company_level' } :
                                            UNKNOWN;
                                    },
                                    menuOptions: {
                                        params: (_params, store, _dispatch) => {
                                            const
                                                { scenarioID, foresightScenarios: scenarios } = store.foresights,
                                                scenario: entities.ForesightScenarioReport | undefined = scenarios?.Data.getByID(scenarioID);

                                            return scenario?.Levels.map(({ ID }) => ({ scenarioID, levelID: ID.toString() })) || [];
                                        }
                                    },
                                    component: <ComponentLoader scope="foresights" component="ForesightScenario" dlls={['highcharts']} />
                                }),
                                c({
                                    path: '/scenarios/:scenarioID',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.summary' },
                                    menuOptions: {
                                        index: 0,
                                        params: (_params, store, _dispatch) => {
                                            const { scenarioID } = store.foresights;

                                            if (scenarioID) { return [{ scenarioID }]; }
                                            return [];
                                        }
                                    },
                                    component: <ComponentLoader scope="foresights" component="ForesightScenario" dlls={['highcharts']} />
                                })
                            ]
                        }),

                        c({
                            path: '/performance_alerts',
                            access: accessFromPackage({
                                functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                            }),
                            title: { id: 'navigation.performance_alerts' },
                            menuOptions: { icon: 'file-chart-column' },
                            component: <ComponentLoader scope="alerts" component="PerformanceManagementAlertList" />,

                            childRoutes: [
                                c({
                                    path: '/performance_alerts/new',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.performance_alert_settings' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="alerts" component="PerformanceManagementAlertSettings" />
                                }),
                                c({
                                    path: '/performance_alerts/:alertID',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.BILLING_ANALYSES_AND_ALERTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.BILLING_ANALYSES_AND_ALERTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.performance_alert_settings' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader scope="alerts" component="PerformanceManagementAlertSettings" />
                                })
                            ]
                        }),

                        c({
                            path: '/market_radar',
                            title: { id: 'navigation.market_radar' },
                            menuOptions: { icon: 'market-radar' },

                            childRoutes: [
                                c({
                                    path: '/market_radar/market_feed/newsroom',
                                    access: AccessFunctionality.NEWS,
                                    title: { id: 'navigation.newsroom' },
                                    menuOptions: {
                                        query: getDefaultNewsroomQuery,
                                        icon: 'newspaper-o'
                                    },
                                    component: <ComponentLoader scope="newsfeed" component="ApprovalFeed" />,
                                    onEnter: ((state, replace) => {
                                        const query = getDefaultNewsroomQuery(state.params, store.getState(), store.dispatch);
                                        if (!Object.keys(state.location.query).length && query !== UNKNOWN) {
                                            replace({ pathname: state.location.pathname, query });
                                        }
                                    }) as EnterHook
                                }),
                                c({
                                    path: '/market_radar/news_ranking/pending',
                                    access: AccessFunctionality.NEWS,
                                    title: { id: 'navigation.insights' },
                                    menuOptions: {
                                        query: getPendingNewsRankingQuery,
                                        icon: 'mail-bulk'
                                    },
                                    component: <ComponentLoader scope="newsfeed" component="RankingFeed" />,
                                    onEnter: ((state, replace) => {
                                        const query = getPendingNewsRankingQuery(state.params, store.getState(), store.dispatch);
                                        if (!Object.keys(state.location.query).length && query !== UNKNOWN) {
                                            replace({ pathname: state.location.pathname, query });
                                        }
                                    }) as EnterHook,
                                    childRoutes: [
                                        c({
                                            path: '/market_radar/news_ranking/pending/:entryID',
                                            access: AccessFunctionality.NEWS,
                                            title: { id: 'common.post' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="newsfeed" component="NewsfeedEntry" filter={{ ...defaults.NewsEntryFilter, EntryStatusIDs: [NewsEntryEntryStatus.APPROVED] }} standalone />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/market_radar/market_map',
                                    access: AccessFunctionality.NEWS,
                                    title: { id: 'navigation.market_map' },
                                    menuOptions: { icon: 'map-o', hidePadding: true },
                                    component: <ComponentLoader scope="market" component="MarketMap" dlls={['gojs']} />
                                }),
                                c({
                                    path: '/market_radar/phenomena',
                                    access: AccessFunctionality.MARKET_ANALYSES,
                                    title: { id: 'navigation.trend_radar' },
                                    menuOptions: {
                                        query: DEFAULT_TREND_TOPICS_QUERY,
                                        icon: 'radar'
                                    },
                                    component: <ComponentLoader scope="trend" component="TrendTopicOverview" dlls={['highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/market_radar/phenomena/topics',
                                            access: AccessFunctionality.MARKET_ANALYSES,
                                            title: { id: 'topics.topics' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="trend" component="TrendTopicList" />
                                        }),
                                        c({
                                            path: '/market_radar/phenomena/:areaID',
                                            access: AccessFunctionality.MARKET_ANALYSES,
                                            title: fromStore('trendTopics.trendCategories.Data.:areaID.Description', { id: 'common.no_pestel_area' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="trend" component="TrendTopicList" />,

                                            childRoutes: [
                                                c({
                                                    path: '/market_radar/phenomena/:areaID/:topicID',
                                                    access: AccessFunctionality.MARKET_ANALYSES,
                                                    title: fromStore('trendTopics.topics.Data.:topicID.Name', { id: 'topics.untitled_topic' }),
                                                    component: <ComponentLoader scope="trend" component="TrendTopicList" />
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                c({
                                    path: '/market_radar/stakeholders',
                                    access: AccessFunctionality.STAKEHOLDERS,
                                    menuOptions: {
                                        icon: 'building-regular'
                                    },
                                    title: { id: 'navigation.stakeholders' },
                                    component: <ComponentLoader scope="stakeholders" component="StakeholderList" />,

                                    childRoutes: [
                                        c({
                                            path: '/market_radar/stakeholders/:typeID/all',
                                            access: AccessFunctionality.STAKEHOLDERS,
                                            title: { id: 'navigation.stakeholders' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="stakeholders" component="StakeholderList" />,

                                            childRoutes: [
                                                c({
                                                    path: '/market_radar/stakeholders/:typeID/all/:stakeholderID',
                                                    access: AccessFunctionality.STAKEHOLDERS,
                                                    title: fromStore('stakeholders.stakeholderTypes.Data.:typeID.Description', { id: 'stakeholders.untitled_stakeholder' }),
                                                    component: <ComponentLoader scope="stakeholders" component="StakeholderList" />
                                                })
                                            ]
                                        }),
                                        c({
                                            path: '/market_radar/stakeholders/:typeID/:groupID',
                                            access: AccessFunctionality.STAKEHOLDERS,
                                            title: fromStore('stakeholders.stakeholderTypes.Data.:typeID.Stakeholders.Data.:groupID.Name', { id: 'corporate_structure.untitled_group' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="stakeholders" component="StakeholderList" />,

                                            childRoutes: [
                                                c({
                                                    path: '/market_radar/stakeholders/:typeID/:groupID/overview',
                                                    access: AccessFunctionality.STAKEHOLDERS,
                                                    title: fromStore('stakeholders.stakeholderTypes.Data.:typeID.Stakeholders.Data.:groupID.Name', { id: 'corporate_structure.untitled_group' }),
                                                    menuOptions: { hidden: true },
                                                    component: <ComponentLoader scope="stakeholders" component="StakeholderGroup" dlls={['highcharts']} />
                                                }),
                                                c({
                                                    path: '/market_radar/stakeholders/:typeID/:groupID/:stakeholderID',
                                                    access: AccessFunctionality.STAKEHOLDERS,
                                                    title: fromStore('stakeholders.stakeholderTypes.Data.:typeID.Stakeholders.Data.:groupID.Name', { id: 'corporate_structure.untitled_group' }),
                                                    menuOptions: { hidden: true },
                                                    component: <ComponentLoader scope="stakeholders" component="StakeholderList" />
                                                })
                                            ]
                                        })
                                    ]
                                }),

                                c({
                                    path: '/market_radar/sources',
                                    access: AccessFunctionality.NEWS_SOURCES,
                                    title: { id: 'navigation.news_sources' },
                                    menuOptions: {
                                        icon: 'network-wired'
                                    },
                                    component: <ComponentLoader scope="newsfeed" component="NewsSources" dlls={['gojs']} />,

                                    childRoutes: [
                                        c({
                                            path: '/market_radar/sources/watches/new',
                                            access: AccessFunctionality.NEWS_SOURCES,
                                            title: { id: 'market_watch_settings.new_watch' },
                                            menuOptions: {
                                                hidden: true,
                                                query: getDefaultNewWatchQuery
                                            },
                                            component: <ComponentLoader scope="market-watch" component="MarketWatchSettings" dlls={['highcharts', 'jquery']} />,
                                            onEnter: (state, replace) => {
                                                const query = getDefaultNewWatchQuery(state.params, store.getState(), store.dispatch);
                                                if (!Object.keys(state.location.query).length && query !== UNKNOWN) {
                                                    replace({ pathname: state.location.pathname, query });
                                                }
                                            }
                                        }),
                                        c({
                                            path: '/market_radar/sources/watches/:watchID',
                                            access: AccessFunctionality.NEWS_SOURCES,
                                            title: fromStore('watches.list.Data.:watchID.Name'),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="market-watch" component="MarketWatchSettings" dlls={['highcharts', 'jquery']} />
                                        }),
                                        c({
                                            path: '/market_radar/sources/rss',
                                            access: AccessFunctionality.NEWS_SOURCES,
                                            title: { id: 'navigation.follow_up.rss' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="market-watch" component="RssSettingsPage" />,

                                            childRoutes: [
                                                c({
                                                    path: '/market_radar/sources/rss/:watchID',
                                                    access: AccessFunctionality.NEWS_SOURCES,
                                                    title: { id: 'navigation.follow_up.rss' },
                                                    component: <ComponentLoader scope="market-watch" component="RssSettingsPage" />
                                                })
                                            ]
                                        }),
                                        c({
                                            path: '/market_radar/sources/bing',
                                            access: AccessFunctionality.NEWS_SOURCES,
                                            title: { id: 'navigation.follow_up.news_watch' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="bing-search" component="BingSearch" />,

                                            childRoutes: [
                                                c({
                                                    path: '/market_radar/sources/bing/:watchID',
                                                    access: AccessFunctionality.NEWS_SOURCES,
                                                    title: fromStore('bingSearch.marketFeedBingCognitives.Data.:watchID.Name'),
                                                    component: <ComponentLoader scope="bing-search" component="BingSearch" />
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        c({
                            path: '/indicators_and_tracking_levels',
                            title: { id: 'navigation.key_performance_indicators' },
                            menuOptions: { icon: 'kpi' },

                            childRoutes: [
                                c({
                                    path: '/indicators_and_tracking_levels/indicators',
                                    access: getIndicatorAndTrackingLevelsAccess,
                                    title: { id: 'navigation.performance_indicators' },
                                    component: <ComponentLoader scope="performance-indicator" component="IndicatorPerspectiveList" />,

                                    childRoutes: [
                                        c({
                                            path: '/indicators_and_tracking_levels/indicators/:perspectiveID',
                                            access: getIndicatorAndTrackingLevelsAccess,
                                            title: fromStore('performanceIndicators.performancePerspectives.Data.:perspectiveID.Description'),
                                            menuOptions: {
                                                hidden: true,
                                                collapsable: true,
                                                params: (
                                                    _params, store, dispatch,
                                                    { performanceIndicators: { performancePerspectives } }:
                                                    { performanceIndicators: { performancePerspectives: entities.NetworkArray<entities.PerformancePerspective>; }; } = store as any
                                                ) => {
                                                    if (performancePerspectives.Custom.status === RequestStatus.UNSYNCED) {
                                                        dispatch(untypedActions.loadAllPerformancePerspectives());
                                                    }

                                                    return performancePerspectives.Data.map(({ ID }) => ({ perspectiveID: `${ID}` }));
                                                },
                                                query: getDefaultIndicatorListQuery
                                            },
                                            component: <ComponentLoader scope="performance-indicator" component="PerformanceIndicatorList" />,

                                            childRoutes: [
                                                c({
                                                    path: '/indicators_and_tracking_levels/indicators/:perspectiveID/:perspectiveGroupID',
                                                    access: getIndicatorAndTrackingLevelsAccess,
                                                    title: (params, store, dispatch) => {
                                                        const groupID = +params.perspectiveGroupID;

                                                        if (groupID !== PerformancePerspectiveGroup.GOALS && groupID !== PerformancePerspectiveGroup.ACTIONS) {
                                                            return fromStore('performanceIndicators.performancePerspectiveGroups.Data.:perspectiveGroupID.Description', { id: 'common.untitled_group' })(params, store, dispatch);
                                                        }

                                                        const { generalSettings } = store.app as any;

                                                        if (generalSettings.Custom.status === RequestStatus.UNSYNCED) {
                                                            dispatch(untypedActions.loadGeneralSettings());
                                                        }

                                                        if (generalSettings.Custom.status === RequestStatus.SUCCEEDED) {
                                                            const strategyNamingTypeID = generalSettings.DefaultStrategyNamingTypeID;

                                                            if (groupID === PerformancePerspectiveGroup.GOALS) {
                                                                return { id: `strategy.naming.goals_${strategyNamingTypeID}` };
                                                            }

                                                            if (groupID === PerformancePerspectiveGroup.ACTIONS) {
                                                                return { id: `strategy.naming.actions_${strategyNamingTypeID}` };
                                                            }
                                                        }

                                                        return UNKNOWN;
                                                    },
                                                    menuOptions: {
                                                        params: (
                                                            { perspectiveID }, store, dispatch,
                                                            { performanceIndicators: { performancePerspectiveGroups } }:
                                                            { performanceIndicators: { performancePerspectiveGroups: entities.NetworkArray<entities.PerformancePerspectiveGroup>; }; } = store as any
                                                        ) => {
                                                            if (performancePerspectiveGroups.Custom.status === RequestStatus.UNSYNCED) {
                                                                dispatch(untypedActions.loadAllPerformancePerspectiveGroups());
                                                            }

                                                            return performancePerspectiveGroups.Data
                                                                .filter(({ PerspectiveID }) => PerspectiveID === +perspectiveID)
                                                                .map(({ ID }) => ({ perspectiveGroupID: `${ID}` }));
                                                        },
                                                        query: getDefaultIndicatorListQuery
                                                    },
                                                    component: <ComponentLoader scope="performance-indicator" component="PerformanceIndicatorList" />
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                c({
                                    path: '/indicators_and_tracking_levels/tracking_levels',
                                    access: getIndicatorAndTrackingLevelsAccess,
                                    title: { id: 'navigation.tracking_levels' },
                                    component: <ComponentLoader scope="corporate-structure" component="TrackingLevelList" />,

                                    childRoutes: [
                                        c({
                                            path: '/indicators_and_tracking_levels/tracking_levels/:levelID',
                                            access: getIndicatorAndTrackingLevelsAccess,
                                            title: fromStore('corporateStructure.trackingLevels.Data.:levelID.Description'),
                                            menuOptions: {
                                                hidden: true,
                                                params: (
                                                    _params, store, dispatch,
                                                    { corporateStructure: { trackingLevels } }: { corporateStructure: { trackingLevels: entities.NetworkArray<entities.TrackingLevel>; }; } = store as any
                                                ) => {
                                                    if (trackingLevels.Custom.status === RequestStatus.UNSYNCED) {
                                                        dispatch(actions.trackingLevels.loadAll() as any); // TODO add proper "dispatch" type
                                                    }

                                                    return trackingLevels.Data
                                                        .filter(({ ID, IsEnabled }) => IsEnabled && ![TrackingLevel.COMPANY_FUNCTION, TrackingLevel.COMPANY_LEGAL_ENTITY].includes(ID))
                                                        .map(({ ID }) => ({ levelID: `${ID}` }));
                                                }
                                            },
                                            component: <ComponentLoader scope="corporate-structure" component="CorporateStructureList" />
                                        })
                                    ]
                                })
                            ]
                        }),
                        c({
                            path: '/data_management',
                            title: { id: 'navigation.data_management' },
                            menuOptions: { icon: 'chart-column-settings' },

                            childRoutes: [
                                c({
                                    path: '/data_management/financial_systems',
                                    access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.financial_systems' },
                                    component: <ComponentLoader scope="integration" component="FinancialSystemsOverview" />,

                                    childRoutes: [
                                        c({
                                            path: '/data_management/financial_systems/internal_accounts',
                                            access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                            title: { id: 'navigation.financial_systems' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="financial-management" component="CorporateCalculationSettings" />
                                        }),
                                        c({
                                            path: '/data_management/financial_systems/manual_exports',
                                            access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                            title: { id: 'navigation.financial_systems' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="financial-management" component="CorporateCalculationSettings" editCorporateAccounts={false} />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/data_management/integrations',
                                    access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                    title: { id: 'navigation.integrations' },
                                    component: <ComponentLoader scope="integration" component="IntegrationSourceList" />,

                                    childRoutes: [
                                        c({
                                            path: `/data_management/integrations/${IntegrationSource.TOGGL}/:rawDataSourceID`,
                                            access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                            title: fromStore('integrations.rawDataSources.Data.:rawDataSourceID.Name', { id: 'integration.untitled_integration' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="RawDataConfiguration" />
                                        }),
                                        c({
                                            path: '/data_management/integrations/:sourceID',
                                            access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                            title: fromStore('integrations.integrationSources.Data.:sourceID.Description', { id: 'integration.untitled_integration' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="IntegrationSourceDetails" />,

                                            childRoutes: [
                                                c({
                                                    path: '/data_management/integrations/:sourceID/:sourceItemID',
                                                    access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                                    title: fromStore('integrations.integrationSources.Data.:sourceID.Description', { id: 'integration.untitled_integration' }),
                                                    component: <ComponentLoader scope="integration" component="IntegrationSourceDetails" dlls={['highcharts']} />,

                                                    childRoutes: [
                                                        c({
                                                            path: '/data_management/integrations/:sourceID/:sourceItemID/:tabID',
                                                            access: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                                            title: fromStore('integrations.integrationSources.Data.:sourceID.Description', { id: 'integration.untitled_integration' }),
                                                            component: <ComponentLoader scope="integration" component="IntegrationSourceDetails" dlls={['highcharts']} />
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                c({
                                    path: '/data_management/polls',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.POLLS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.POLLS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.surveys' },
                                    component: <ComponentLoader scope="dashboard" component="PollList" dlls={['highcharts']} />,

                                    childRoutes: [
                                        c({
                                            path: '/data_management/polls/:pollSettingID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.POLLS,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.POLLS, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('polls.pollSettingsList.Data.:pollSettingID.Name', { id: 'navigation.stakeholder_input.poll_detail_page' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="dashboard" component="PollDetails" dlls={['highcharts']} />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/data_management/budgets_and_forecasts',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.FORECASTS,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.budgets_and_forecasts' },
                                    component: <ComponentLoader scope="integration" component="ForecastList" />,

                                    childRoutes: [
                                        c({
                                            path: '/data_management/budgets_and_forecasts/quick_import',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.DATA_SOURCES,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: accessFromUserRole(AccessFunctionality.DATA_SOURCES, ...NON_PARTICIPANT_ROLES)
                                            }),
                                            title: { id: 'navigation.quick_import' },
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="ExcelQuickImport" />
                                        }),
                                        c({
                                            path: '/data_management/budgets_and_forecasts/new/:modelID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.FORECASTS,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('forecasts.forecastModels.Data.:modelID.Name', { id: 'integration.untitled_forecast' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="ForecastModel" />,

                                            childRoutes: [
                                                c({
                                                    path: '/data_management/budgets_and_forecasts/new/:modelID/:levelID',
                                                    access: accessFromPackage({
                                                        functionality: AccessFunctionality.FORECASTS,
                                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                    }),
                                                    title: fromStore('forecasts.forecastModels.Data.:modelID.Levels.:levelID.Name', { id: 'integration.untitled_file' }),
                                                    component: <ComponentLoader scope="integration" component="ForecastModelAggregationReport" />
                                                }),
                                                c({
                                                    path: `/data_management/budgets_and_forecasts/new/:modelID/:levelID/:entryID/${ForecastModelDataEntryType.APPLICATION}`,
                                                    access: accessFromPackage({
                                                        functionality: AccessFunctionality.FORECASTS,
                                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                    }),
                                                    title: (params, store, dispatch) => {
                                                        if (+params.entryID) {
                                                            return fromStore('forecasts.forecastModels.Data.:modelID.Levels.:levelID.Entries.:entryID.Name', { id: 'integration.untitled_file' })(params, store, dispatch);
                                                        }
                                                        else {
                                                            const model = store.forecasts.forecastModels.Data.getByID(+params.modelID),
                                                                level = model ? model.Levels.getByID(+params.levelID) : null,
                                                                title = level ? getCorporateStructureLinkDescription(store.corporateStructure, level.CorporateStructureLink) : '';

                                                            return title || UNKNOWN;
                                                        }
                                                    },
                                                    component: <ComponentLoader scope="performance-indicator" component="ProfitAndLossStatement" />
                                                })
                                            ]
                                        }),
                                        c({
                                            path: '/data_management/budgets_and_forecasts/record/:recordID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.FORECASTS,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('forecasts.forecastModelRecordDatas.Data.:recordID.Name', { id: 'integration.untitled_forecast' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="ForecastModelRecord" />
                                        }),
                                        c({
                                            path: '/data_management/budgets_and_forecasts/:forecastID',
                                            access: accessFromPackage({
                                                functionality: AccessFunctionality.FORECASTS,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                            }),
                                            title: fromStore('sheets.sheetForecasts.Data.:forecastID.Name', { id: 'integration.untitled_forecast' }),
                                            menuOptions: { hidden: true },
                                            component: <ComponentLoader scope="integration" component="SheetForecastFileDetails" />,

                                            childRoutes: [
                                                c({
                                                    path: '/data_management/budgets_and_forecasts/:forecastID/:forecastFileID',
                                                    access: accessFromPackage({
                                                        functionality: AccessFunctionality.FORECASTS,
                                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                    }),
                                                    title: fromStore('sheets.sheetForecasts.Data.:forecastID.Files.:forecastFileID.Name', { id: 'integration.untitled_file' }),
                                                    component: <ComponentLoader scope="integration" component="SheetForecastAggregationPreview" />
                                                }),
                                                c({
                                                    path: '/data_management/budgets_and_forecasts/:forecastID/:forecastFileID/input',
                                                    access: accessFromPackage({
                                                        functionality: AccessFunctionality.FORECASTS,
                                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.FORECASTS, ...NON_PARTICIPANT_ROLES),
                                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN
                                                    }),
                                                    title: fromStore('sheets.sheetForecasts.Data.:forecastID.Files.:forecastFileID.Name', { id: 'integration.untitled_file' }),
                                                    component: <ComponentLoader scope="performance-indicator" component="ProfitAndLossStatement" />
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                getTableAutomationRoutes('/data_management', false),
                                c({
                                    path: '/data_management/profit_and_loss',
                                    access: accessFromPackage({
                                        functionality: AccessFunctionality.INDICATOR_MANAGEMENT,
                                        [ProductPackage.USER_ROLE_BASED_BILLING]: accessFromUserRole(AccessFunctionality.INDICATOR_MANAGEMENT, ...NON_PARTICIPANT_ROLES),
                                        [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: RouteAccessType.HIDDEN,
                                        [ProductPackage.GOALWISOR]: RouteAccessType.HIDDEN
                                    }),
                                    title: { id: 'navigation.input_in_profit_and_loss_format' },
                                    menuOptions: { hidden: true },
                                    component: <ComponentLoader component="ProfitAndLossStatement" scope="performance-indicator" />
                                })
                            ]
                        }),

                        c({
                            path: '/user_management',
                            access: (_, store) => store.app.session.ProductPackageID === ProductPackage.SMART_STRATEGY && store.app.session.IsUserAdministrator ?
                                RouteAccessType.FULL :
                                RouteAccessType.HIDDEN,
                            title: { id: 'navigation.users_and_access' },
                            menuOptions: { icon: 'users-gear-solid' },
                            component: <ComponentLoader scope="users" component="UserList" />
                        }),

                        c({
                            path: '/demo',
                            access: RouteAccessType.FULL,
                            title: 'Demo',
                            beta: true,
                            menuOptions: { icon: 'bug', hidden: !DEBUG },
                            component: <ComponentLoader scope="demo" component="Demo" dlls={['highcharts', 'codemirror']} />
                        }),

                        c({
                            path: '/partner_management',
                            title: { id: 'partner_management.partner_management' },

                            childRoutes: [
                                c({
                                    path: '/partner_management/companies',
                                    access: (_params, { app: { session } }) => session.UserID && session.HasPartnerPanel ? RouteAccessType.FULL : RouteAccessType.HIDDEN,
                                    title: { id: 'partner_management.client_companies' },
                                    component: <ComponentLoader scope="partner-management" component="ClientCompanyList" />
                                }),
                                c({
                                    path: '/partner_management/users',
                                    access: (_params, { app: { session } }) => session.UserID && session.HasPartnerPanel ? RouteAccessType.FULL : RouteAccessType.HIDDEN,
                                    title: { id: 'partner_management.users_and_accesses' },
                                    component: <ComponentLoader scope="partner-management" component="PartnerUserList" />
                                }),
                                c({
                                    path: '/partner_management/agreement',
                                    access: (_params, { app: { session } }) => session.UserID && session.HasPartnerPanel && session.IsPartnerCompanyAdministrator ? RouteAccessType.FULL : RouteAccessType.HIDDEN,
                                    title: { id: 'partner_management.agreement' },
                                    component: <ComponentLoader scope="partner-management" component="PartnerAgreement" />
                                })
                            ]
                        }),

                        c({
                            path: '/organization',
                            title: { id: 'navigation.system_settings' },

                            childRoutes: [
                                c({
                                    path: '/organization/general_settings',
                                    title: { id: 'navigation.general_settings' },

                                    childRoutes: [
                                        c({
                                            path: '/organization/general_settings/strategy',
                                            access: getStrategySettingsAccess,
                                            title: { id: 'navigation.strategy_settings' },
                                            component: <ComponentLoader scope="strategy" component="StrategySettings" />
                                        }),
                                        c({
                                            path: '/organization/general_settings/company_information',
                                            access: accessFromSession(USER_ADMINISTRATION),
                                            title: { id: 'navigation.company_information' },
                                            component: <ComponentLoader scope="company" component="Company" />
                                        }),
                                        c({
                                            path: '/organization/general_settings/subscription',
                                            access: (params, store, dispatch) =>
                                                store.app.session.CompanyTemplateTypeID === DEFAULT_COMPANY_TEMPLATE_TYPE ||
                                                    store.app.session.CompanyTemplateTypeID === ADVISORY_TEMPLATE_TYPE ?
                                                    accessFromSession(USER_ADMINISTRATION)(params, store, dispatch) :
                                                    RouteAccessType.DISABLED,
                                            title: { id: 'navigation.subscription' },
                                            component: <ComponentLoader scope="subscription" component="SubscriptionSettings" />
                                        }),
                                        c({
                                            path: '/organization/general_settings/client_company_creation',
                                            access: accessFromSession(USER_ADMINISTRATION),
                                            title: { id: 'navigation.create_new_client' },
                                            component: <ComponentLoader scope="company" component="ClientCompanyCreation" />
                                        })
                                    ]
                                }),
                                c({
                                    path: '/organization/users',
                                    title: { id: 'navigation.users_and_organization_structure' },

                                    childRoutes: [
                                        c({
                                            path: '/organization/users/users',
                                            access: accessFromPackage({
                                                functionality: USER_ADMINISTRATION,
                                                [ProductPackage.USER_ROLE_BASED_BILLING]: (params, store, dispatch) =>
                                                    store.app.session.UserRoleBillingUserTypeID !== UserRoleBillingUserType.MANAGER ?
                                                        accessFromSession(USER_ADMINISTRATION)(params, store, dispatch) :
                                                        RouteAccessType.FULL,
                                                [ProductPackage.SMART_STRATEGY]: RouteAccessType.HIDDEN,
                                                [ProductPackage.LIGHTWEIGHT_MUNICIPALITY]: (params, store, dispatch) =>
                                                    store.app.session.UserRoleBillingUserTypeID !== UserRoleBillingUserType.MANAGER ?
                                                        accessFromSession(USER_ADMINISTRATION)(params, store, dispatch) :
                                                        RouteAccessType.FULL
                                            }),
                                            title: { id: 'navigation.users_and_access' },
                                            component: <ComponentLoader scope="users" component="UserList" />
                                        }),
                                        c({
                                            path: '/organization/users/organization_structure',
                                            access: accessFromPackage({
                                                functionality: USER_ADMINISTRATION,
                                                [ProductPackage.SMART_STRATEGY]: (_params, store, _dispatch) =>
                                                    store.app.session.IsUserAdministrator &&
                                                    store.app.session.FunctionalityAddOns.includes(AccessFunctionalityAddOn.TEAM_MANAGEMENT) ?
                                                        RouteAccessType.FULL :
                                                        RouteAccessType.DISABLED
                                            }),
                                            title: { id: 'navigation.organization_structure' },
                                            menuOptions: { hidePadding: true },
                                            component: <ComponentLoader scope="organization-structure" component="OrganizationStructure" dlls={['gojs']} />
                                        }),
                                        c({
                                            path: '/organization/users/activity_report',
                                            access: (params, store, dispatch) =>
                                                store.app.session.UseUserRoleBasedBilling &&
                                                store.app.session.UserRoleBillingUserTypeID === UserRoleBillingUserType.FACILITATOR ?
                                                    RouteAccessType.FULL :
                                                    accessFromSession(USER_ADMINISTRATION)(params, store, dispatch),
                                            title: { id: 'navigation.activity_report' },
                                            component: <ComponentLoader scope="users" component="ActivityReport" dlls={['aggrid']} />
                                        })
                                    ]
                                })
                            ]
                        }),

                        // c({
                        //    title: 'Non-menu routes',
                        //    menuOptions: { hidden: true },

                        //    childRoutes: []
                        // }),

                        c({
                            title: 'Backward compatibility',
                            menuOptions: { hidden: true },
                            redirect: '/',

                            childRoutes: buildRedirects({
                                '/business_insight/metrics/forecasts':
                            '/data_management/table_automation/forecasts',
                                '/business_insight/metrics/forecasts/:forecastID':
                            '/data_management/table_automation/forecasts/:forecastID',
                                '/business_insight/metrics/forecasts/:forecastID/:forecastFileID':
                            '/data_management/table_automation/forecasts/:forecastID/:forecastFileID',
                                '/business_insight/metrics/forecasts/:forecastID/:forecastFileID/input':
                            '/data_management/table_automation/forecasts/:forecastID/:forecastFileID/input',
                                '/business_insight/business/polls':
                            '/data_management/data_entry/polls',
                                '/business_insight/business/polls/:pollSettingID':
                            '/data_management/data_entry/polls/:pollSettingID',
                                '/business_insight/metrics/performance_indicators':
                            '/data_management/indicators_and_tracking_levels/indicators',
                                '/business_insight/metrics/performance_indicators/profit_and_loss':
                            '/data_management/profit_and_loss',
                                '/business_insight/metrics/tracking_levels':
                            '/data_management/indicators_and_tracking_levels/tracking_levels',
                                '/business_insight/metrics/tracking_levels/:levelID':
                            '/data_management/indicators_and_tracking_levels/tracking_levels/:levelID',
                                '/market_environment_monitoring/analyses/insights':
                            '/agenda360/insight_feed',
                                '/market_environment_monitoring/analyses/insights/:companyFunctionID':
                            '/agenda360/insight_feed',
                                '/strategy/actions/initiatives':
                            '/agenda360/initiatives',
                                '/strategy/actions/initiatives/:companyFunctionID':
                            '/agenda360/initiatives/:companyFunctionID',
                                '/strategy/strategy_execution/initiatives':
                            '/agenda360/initiatives',
                                '/strategy/actions/meetings':
                            '/agenda360',
                                '/strategy/actions/meetings/:companyFunctionID/:sourceID/:meetingID':
                            '/agenda360/meetings/:companyFunctionID/:sourceID/:meetingID',
                                '/strategy/actions/meetings/:companyFunctionID/:sourceID/:meetingID/:sectionID':
                            '/agenda360/meetings/:companyFunctionID/:sourceID/:meetingID/:sectionID',
                                '/agenda360/:companyFunctionID/meetings/:sourceID/:meetingID':
                            '/agenda360/meetings/:companyFunctionID/:sourceID/:meetingID',
                                '/agenda360/:companyFunctionID/meetings/:sourceID/:meetingID/:sectionID':
                            '/agenda360/meetings/:companyFunctionID/:sourceID/:meetingID/:sectionID',
                                '/agenda360/my_agenda/responsible_tasks':
                            '/agenda360/overview',
                                '/agenda360/:companyFunctionID':
                            '/agenda360/insight_feed',
                                '/agenda360/:companyFunctionID/overview':
                            '/agenda360/overview/:companyFunctionID',
                                '/agenda360/:companyFunctionID/business_review':
                            '/agenda360/business_review/:companyFunctionID',
                                '/agenda360/:companyFunctionID/insights':
                            '/agenda360/insight_feed',
                                '/performance/dashboards':
                            '/business_insight/metrics/dashboards',
                                '/performance/dashboards/:pageID':
                            '/business_insight/metrics/dashboards/:pageID',
                                '/business_insight/metrics/dashboards':
                            '/scorecards',
                                '/home_page_configuration':
                            '/scorecards/homepage/management',
                                '/business_insight/metrics/data_sources':
                            '/data_management/external_systems/other_systems',
                                [`/business_insight/metrics/data_sources/${IntegrationSource.TOGGL}/:rawDataSourceID`]:
                            `/data_management/external_systems/other_systems/${IntegrationSource.TOGGL}/:rawDataSourceID`,
                                '/business_insight/metrics/data_sources/:sourceID':
                            '/data_management/external_systems/other_systems/:sourceID',
                                '/business_insight/metrics/data_sources/:sourceID/:sourceItemID':
                            '/data_management/external_systems/other_systems/:sourceID/:sourceItemID',
                                '/business_insight/metrics/data_sources/:sourceID/:sourceItemID/:tabID':
                            '/data_management/external_systems/other_systems/:sourceID/:sourceItemID/:tabID',
                                '/organization/settings/integrations':
                            '/data_management/external_systems/other_systems',
                                [`/organization/settings/integrations/${IntegrationSource.TOGGL}/:rawDataSourceID`]:
                            `/data_management/external_systems/other_systems/${IntegrationSource.TOGGL}/:rawDataSourceID`,
                                '/organization/settings/integrations/:sourceID':
                            '/data_management/external_systems/other_systems/:sourceID',
                                '/organization/settings/integrations/:sourceID/:sourceItemID':
                            '/data_management/external_systems/other_systems/:sourceID/:sourceItemID',
                                '/organization/settings/integrations/:sourceID/:sourceItemID/:tabID':
                            '/data_management/external_systems/other_systems/:sourceID/:sourceItemID/:tabID',
                                [`/business_insight/metrics/data_sources/${IntegrationSource.GOOGLE_SHEETS}`]:
                            `/data_management/table_automation/tables/${IntegrationSource.GOOGLE_SHEETS}`,
                                [`/business_insight/metrics/data_sources/${IntegrationSource.GOOGLE_SHEETS}/:sourceItemID`]:
                            `/data_management/table_automation/tables/${IntegrationSource.GOOGLE_SHEETS}/:sourceItemID`,
                                [`/business_insight/metrics/data_sources/${IntegrationSource.EXCEL}`]:
                            `/data_management/table_automation/tables/${IntegrationSource.EXCEL}`,
                                [`/business_insight/metrics/data_sources/${IntegrationSource.EXCEL}/:sourceItemID`]:
                            `/data_management/table_automation/tables/${IntegrationSource.EXCEL}/:sourceItemID`,
                                '/business_insight/metrics/performance_alerts':
                            '/scorecards/information_sharing/performance_alerts',
                                '/market_insight/market/stakeholders':
                            '/market_radar/stakeholders',
                                '/market_insight/market/stakeholders/:typeID/:groupID':
                            '/market_radar/stakeholders/:typeID/:groupID',
                                '/market_insight/market/stakeholders/:typeID/:groupID/:stakeholderID':
                            '/market_radar/stakeholders/:typeID/:groupID/:stakeholderID',
                                '/market_insight/news/newsroom':
                            '/market_radar/market_feed/newsroom',
                                '/market_insight/news/ranking':
                            '/market_radar/news_ranking/pending',
                                '/market_insight/news/ranking/:entryID':
                            '/market_radar/news_ranking/pending/:entryID',
                                '/market_radar/news_ranking/all':
                            '/market_radar/news_ranking/pending',
                                '/market_radar/news_ranking/all/:entryID':
                            '/market_radar/news_ranking/pending/:entryID',
                                '/strategy/strategy_work/swot':
                            '/strategy/analyses/swot',
                                '/strategy/strategy_work/swot/:sessionID':
                            '/strategy/analyses/swot/:sessionID',
                                '/strategy/strategy_work/swot/:sessionID/topics':
                            '/strategy/analyses/swot/:sessionID/topics',
                                '/strategy/strategy_work/swot/:sessionID/:areaID':
                            '/strategy/analyses/swot/:sessionID/:areaID',
                                '/strategy/strategy_work/swot/:sessionID/:areaID/:topicID':
                            '/strategy/analyses/swot/:sessionID/:areaID/:topicID',
                                '/market_insight/news/sources':
                            '/market_radar/sources',
                                '/market_insight/news/sources/watches/new':
                            '/market_radar/sources/watches/new',
                                '/market_insight/news/sources/watches/:watchID':
                            '/market_radar/sources/watches/:watchID',
                                '/market_insight/news/sources/rss':
                            '/market_radar/sources/rss',
                                '/market_insight/news/sources/rss/:watchID':
                            '/market_radar/sources/rss/:watchID',
                                '/market_insight/news/sources/bing':
                            '/market_radar/sources/bing',
                                '/market_insight/news/sources/bing/:watchID':
                            '/market_radar/sources/bing/:watchID',
                                '/strategic_management/decision_making_and_goals':
                            '/agenda360',
                                '/workshops/:workshopID/phases': '/workshops/:workshopID',
                                '/agenda360/:companyFunctionID/meetings': '/agenda360/calendar/:companyFunctionID',
                                '/strategy/workshops': '/agenda360/workshops',
                                '/scorecards/analisys_tools': '/scorecards/analysis_tools',
                                '/business_insight/metrics/products_and_customers': '/scorecards/analysis_tools/billing_analyses/products_and_customers',
                                '/scorecards/analisys_tools/billing_analyses/products_and_customers': '/scorecards/analysis_tools/billing_analyses/products_and_customers',
                                '/business_insight/metrics/map_dashboard': '/scorecards/analysis_tools/billing_analyses/map',
                                '/scorecards/analisys_tools/billing_analyses/map': '/scorecards/analysis_tools/billing_analyses/map',
                                '/strategy/strategy_execution/scenarios': '/scenarios',
                                '/strategy/strategy_execution/scenarios/:scenarioID': '/scenarios/:scenarioID',
                                '/strategy/strategy_execution/scenarios/:scenarioID/cashflow': '/scenarios/:scenarioID/cashflow',
                                '/strategy/strategy_execution/scenarios/:scenarioID/summary': '/scenarios/:scenarioID/summary',
                                '/strategy/strategy_execution/scenarios/:scenarioID/:levelID': '/scenarios/:scenarioID/:levelID',
                                '/market_insight/market/stakeholders/:typeID': '/market_radar/stakeholders/:typeID/all',
                                '/market_insight/market/trend_topics': '/market_radar/phenomena',
                                '/market_insight/market/trend_topics/topics': '/market_radar/phenomena/topics',
                                '/market_insight/market/trend_topics/:areaID': '/market_radar/phenomena/:areaID',
                                '/market_insight/market/trend_topics/:areaID/:topicID': '/market_radar/phenomena/:areaID/:topicID',
                                '/strategy/analyses/phenomena': '/market_radar/phenomena',
                                '/strategy/analyses/phenomena/topics': '/market_radar/phenomena/topics',
                                '/strategy/analyses/phenomena/:areaID': '/market_radar/phenomena/:areaID',
                                '/strategy/analyses/phenomena/:areaID/:topicID': '/market_radar/phenomena/:areaID/:topicID',
                                '/market_insight/market/foresight': '/scenarios',
                                '/market_insight/market/foresight/:scenarioID': '/scenarios/:scenarioID',
                                '/strategy/strategy_work/strategy_navigator': '/strategy/strategy_execution',
                                '/strategy/strategy_work/timeline': '/strategy/strategy_execution',
                                '/strategy/strategy_work/strategy_navigator/:strategyID': '/strategy/strategy_execution',
                                '/strategy/strategy_work/strategy_navigator/:strategyID/:programID': '/strategy/strategy_execution',
                                '/strategy/strategy_work/timeline/:strategyID': '/strategy/strategy_execution',
                                '/strategy/strategy_work/timeline/:strategyID/:milestoneID': '/strategy/strategy_execution',
                                '/strategy/strategy_work/strategy_planner': '/strategy/strategy_execution',
                                '/strategy/strategy_work/strategy_planner/:strategyID': '/strategy/strategy_execution',
                                '/strategy/strategy_work/settings': '/strategy/strategy_execution',
                                '/strategy/strategy_work/settings/:strategyID': '/strategy/strategy_execution',
                                '/strategy/strategy_execution/actions': '/strategy/strategy_execution',
                                '/agenda360/overview': '/agenda360/insight_feed',
                                '/agenda360/overview/:companyFunctionID': '/agenda360/insight_feed/:companyFunctionID',
                                '/strategy_board': '/agenda360/insights',
                                '/scorecards/information_sharing/performance_alerts': '/performance_alerts',
                                '/scorecards/information_sharing/performance_alerts/new': '/performance_alerts/new',
                                '/scorecards/information_sharing/performance_alerts/:alertID': '/performance_alerts/:alertID',
                                '/agenda360/calendar/:companyFunctionID': '/calendar/:companyFunctionID',
                                '/scorecards/analysis_tools/scenarios': '/scenarios',
                                '/scorecards/analysis_tools/scenarios/:scenarioID': '/scenarios/:scenarioID',
                                '/scorecards/analysis_tools/scenarios/:scenarioID/cashflow': '/scenarios/:scenarioID/cashflow',
                                '/scorecards/analysis_tools/scenarios/:scenarioID/summary': '/scenarios/:scenarioID/summary',
                                '/scorecards/analysis_tools/scenarios/:scenarioID/:levelID': '/scenarios/:scenarioID/:levelID',
                                '/data_management/overview': '/data_management',
                                '/data_management/indicators_and_tracking_levels': '/indicators_and_tracking_levels',
                                '/data_management/indicators_and_tracking_levels/indicators': '/indicators_and_tracking_levels/indicators',
                                '/data_management/indicators_and_tracking_levels/indicators/:perspectiveID': '/indicators_and_tracking_levels/indicators/:perspectiveID',
                                '/data_management/indicators_and_tracking_levels/indicators/:perspectiveID/:perspectiveGroupID':
                                    '/indicators_and_tracking_levels/indicators/:perspectiveID/:perspectiveGroupID',
                                '/data_management/indicators_and_tracking_levels/tracking_levels': '/indicators_and_tracking_levels/tracking_levels',
                                '/data_management/indicators_and_tracking_levels/tracking_levels/:levelID': '/indicators_and_tracking_levels/tracking_levels/:levelID',
                                '/data_management/data_entry': '/data_management/polls',
                                '/data_management/data_entry/polls': '/data_management/polls',
                                '/data_management/data_entry/polls/:pollSettingID': '/data_management/polls/:pollSettingID',
                                '/data_management/table_automation/forecasts': '/data_management/budgets_and_forecasts',
                                '/data_management/table_automation/forecasts/new/:modelID': '/data_management/budgets_and_forecasts/new/:modelID',
                                '/data_management/table_automation/forecasts/new/:modelID/:levelID': '/data_management/budgets_and_forecasts/new/:modelID/:levelID',
                                [`/data_management/table_automation/forecasts/new/:modelID/:levelID/:entryID/${ForecastModelDataEntryType.APPLICATION}`]:
                                    `/data_management/budgets_and_forecasts/new/:modelID/:levelID/:entryID/${ForecastModelDataEntryType.APPLICATION}`,
                                '/data_management/table_automation/forecasts/record/:recordID': '/data_management/budgets_and_forecasts/record/:recordID',
                                '/data_management/table_automation/forecasts/:forecastID': '/data_management/budgets_and_forecasts/:forecastID',
                                '/data_management/table_automation/forecasts/:forecastID/:forecastFileID': '/data_management/budgets_and_forecasts/:forecastID/:forecastFileID',
                                '/data_management/table_automation/forecasts/:forecastID/:forecastFileID/input':
                                    '/data_management/budgets_and_forecasts/:forecastID/:forecastFileID/input',
                                '/data_management/external_systems': '/data_management',
                                '/data_management/external_systems/financial_system': '/data_management/financial_systems',
                                '/data_management/external_systems/financial_system/internal_accounts': '/data_management/financial_systems/internal_accounts',
                                '/data_management/external_systems/financial_system/manual_exports': '/data_management/financial_systems/manual_exports',
                                '/data_management/external_systems/other_systems': '/data_management/integrations',
                                [`/data_management/external_systems/other_systems/${IntegrationSource.TOGGL}/:rawDataSourceID`]:
                                    `/data_management/integrations/${IntegrationSource.TOGGL}/:rawDataSourceID`,
                                '/data_management/external_systems/other_systems/:sourceID': '/data_management/integrations/:sourceID',
                                '/data_management/external_systems/other_systems/:sourceID/:sourceItemID': '/data_management/integrations/:sourceID/:sourceItemID',
                                '/data_management/external_systems/other_systems/:sourceID/:sourceItemID/:tabID': '/data_management/integrations/:sourceID/:sourceItemID/:tabID',
                                '/scorecards/dashboards': '/scorecards',
                                '/strategy/strategy_execution/indicators': '/strategy/indicators',
                                '/strategy/strategy_execution/indicators/:strategyID': '/strategy/indicators',
                                '/strategy/strategy_execution': '/strategy_execution/roadmap',
                                '/data_management/table_automation/quick_import': '/data_management/budgets_and_forecasts/quick_import',
                                '/strategy/strategy_execution/strategy_settings': '/organization/general_settings/strategy',
                                '/dialogs': '/group_collaboration',
                                '/dialogs/settings': '/group_collaboration/ongoing',
                                '/dialogs/settings/:dialogScheduleID': '/group_collaboration/schedules/:dialogScheduleID',
                                '/dialogs/templates': '/group_collaboration/templates',
                                '/workshops_and_templates': '/group_collaboration',
                                '/workshops_and_templates/open_workshops': '/group_collaboration/ongoing',
                                '/workshops_and_templates/completed_workshops': '/group_collaboration/completed',
                                '/workshops_and_templates/templates': '/group_collaboration/templates',
                                '/strategy/business_review/:periodID': '/strategy/status_updates',
                                '/strategy/action_updates': '/strategy/status_updates',
                                '/strategy_execution/business_review': '/strategy_execution/execution_review',
                                '/workshops/:workshopID/results': '/workshops/:workshopID'
                            }).reverse()
                        }),

                        c({
                            path: '/',
                            menuOptions: { hidden: true },
                            // TODO make `/` a parent of all Layout'ed routes, so it would redirect to a first accessible child naturally
                            // it requires to change how menu is built, currently it doesn't support redirect routes as root
                            // for now an explicit list of possible redirects
                            // these should be redirects from an impossible route to some existing ones in order of preference, "first accessible child" thingie will choose the proper one
                            childRoutes: [
                                c({
                                    path: '/_agenda360',
                                    redirect: '/agenda360'
                                }),
                                c({
                                    path: '/_calendar',
                                    redirect: '/calendar'
                                }),
                                c({
                                    path: '/_workshops_and_templates',
                                    redirect: '/group_collaboration'
                                }),
                                c({
                                    path: '/_strategy_execution',
                                    redirect: '/strategy_execution'
                                }),
                                c({
                                    path: '/_strategy',
                                    redirect: '/strategy'
                                }),
                                c({
                                    path: '/_scorecards',
                                    redirect: '/scorecards'
                                }),
                                c({
                                    path: '/_analysis_tools',
                                    redirect: '/analysis_tools'
                                }),
                                c({
                                    path: '/_performance_alerts',
                                    redirect: '/performance_alerts'
                                }),
                                c({
                                    path: '/_market_radar',
                                    redirect: '/market_radar'
                                }),
                                c({
                                    path: '/_indicators_and_tracking_levels',
                                    redirect: '/indicators_and_tracking_levels'
                                }),
                                c({
                                    path: '/_data_management',
                                    redirect: '/data_management'
                                }),
                                c({
                                    path: '/_not_found',
                                    // this one doesn't exist, but should be here as a fallback if nothing is accessible
                                    // when `/` wraps all the routes, `*` will be the one
                                    redirect: '/not_found'
                                })
                            ]
                        }),

                        c({
                            path: '*',
                            access: RouteAccessType.FULL,
                            title: '',
                            menuOptions: { hidden: true },
                            component: <NoServicesAvailable />
                        })
                    ]
                })
            ]
        })
    ]);

export const publicRoutes = publicPageRoutes;
export default allRoutes;
